// src/pages/Permisos/AdminHub.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Paper, Typography, TextField, Button, IconButton, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox,
    Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, CircularProgress, List, ListItemButton, ListItemIcon,
    ListItemText, Divider, Chip, Collapse, Accordion, AccordionSummary, AccordionDetails,
    TableSortLabel, Autocomplete,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InventoryIcon from '@mui/icons-material/Inventory';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import PlaceIcon from '@mui/icons-material/Place';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LockResetIcon from '@mui/icons-material/LockReset';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { usePermisos } from '../../utils/usePermisos';
import { nodeApiPath } from '../../utils/paths';
import { triggerSessionExpired, solicitudesResetListarJWT, solicitudesResetProcesarJWT } from '../../api/endpoints';
import { parseFechaServer } from '../../utils/fechas';
import UsuariosPermisos from './UsuariosPermisos';
import { HTML_NOVEDADES_HISTORICO } from './templates/bannerNovedadesHistorico';

// ─── Constants ───
const PRIMARY = '#b71c1c';
const PRIMARY_LIGHT = '#fbe9e7';
const PRIMARY_DARK = '#7f0000';
const BORDER_RADIUS = '4px';
const NAV_WIDTH = 220;

const titleCase = (str) => {
    if (!str) return '';
    if (str.includes('.') || str.length <= 5) return str;
    if (str === str.toUpperCase() && str.length > 5) {
        return str.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
    }
    return str;
};

// ─── JWT Fetch helper ───
async function jwtFetch(path, options = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No hay token JWT'); }
    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            'Accept': 'application/json',
            ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    const data = await res.json();
    if (res.status === 401) {
        localStorage.removeItem('SIGIF_JWT_TOKEN');
        window.dispatchEvent(new CustomEvent('sigif-session-expired'));
        throw new Error('Token expirado');
    }
    return data;
}

// ─── Snackbar helper ───
function SnackbarNotif({ open, msg, severity, onClose }) {
    return (
        <Snackbar open={open} autoHideDuration={4000} onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={onClose} severity={severity} variant="filled"
                sx={{ borderRadius: BORDER_RADIUS, width: '100%' }}>
                {msg}
            </Alert>
        </Snackbar>
    );
}

// ─── Dialog with colored header ───
function ColorDialog({ open, onClose, title, children, actions, maxWidth = 'sm', icon }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth
            PaperProps={{ sx: { borderRadius: BORDER_RADIUS, overflow: 'hidden' } }}>
            <DialogTitle sx={{
                bgcolor: PRIMARY, color: '#fff', display: 'flex', alignItems: 'center', gap: 1,
                py: 1.5, px: 2
            }}>
                {icon}
                <span style={{ fontWeight: 600, color: '#fff', fontSize: '1rem', flex: 1 }}>{title}</span>
                <IconButton onClick={onClose} sx={{ ml: 'auto', color: '#fff' }} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important', pb: 1 }}>{children}</DialogContent>
            {actions && <DialogActions sx={{ px: 2, pb: 2 }}>{actions}</DialogActions>}
        </Dialog>
    );
}

// ─── Sort hook (reusable) ───
function useSort(defaultCol = '', defaultDir = 'asc') {
    const [col, setCol] = useState(defaultCol);
    const [dir, setDir] = useState(defaultDir);
    const toggle = (c) => { if (col === c) setDir(d => d === 'asc' ? 'desc' : 'asc'); else { setCol(c); setDir('asc'); } };
    const sorted = (rows) => {
        if (!col || !rows?.length) return rows;
        return [...rows].sort((a, b) => {
            let va = a[col] ?? '', vb = b[col] ?? '';
            if (typeof va === 'number' && typeof vb === 'number') return dir === 'asc' ? va - vb : vb - va;
            va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
            return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        });
    };
    return { col, dir, toggle, sorted };
}

const SortHead = ({ sort, field, children, sx: sxProp, ...rest }) => (
    <TableCell sx={sxProp} {...rest}>
        <TableSortLabel active={sort.col === field} direction={sort.col === field ? sort.dir : 'asc'} onClick={() => sort.toggle(field)}
            sx={{ '&.Mui-active': { color: PRIMARY }, '& .MuiTableSortLabel-icon': { color: PRIMARY + ' !important' } }}>
            {children}
        </TableSortLabel>
    </TableCell>
);

// ═══════════════════════════════════════════════════════════
// CATALOGOS SECTION
// ═══════════════════════════════════════════════════════════
const CATALOGO_GROUPS = [
    {
        label: 'Calendario', icon: <CalendarMonthIcon fontSize="small" />,
        items: ['feria-judicial', 'feriados'].sort()
    },
    {
        label: 'Carga Masiva', icon: <SwapHorizIcon fontSize="small" />,
        items: ['cp-departamentales', 'cp-justicia-paz', 'parametros-carga-masiva'].sort()
    },
    {
        label: 'Custodia', icon: <InventoryIcon fontSize="small" />,
        items: ['custodia-estados', 'custodia-mov-tipos'].sort()
    },
    {
        label: 'Documentos', icon: <DescriptionIcon fontSize="small" />,
        items: ['biblioteca-refs', 'biblioteca-tipos', 'escritos-categorias'].sort()
    },
    {
        label: 'Expedientes', icon: <GavelIcon fontSize="small" />,
        items: ['caracteres', 'estados-proceso', 'objetos-reclamo', 'tipos-proceso'].sort()
    },
    {
        label: 'Geografía', icon: <PlaceIcon fontSize="small" />,
        items: ['codigos-postales', 'codigos-postales-departamentos', 'localidades', 'provincias'].sort()
    },
    {
        label: 'Institucional', icon: <ApartmentIcon fontSize="small" />,
        items: ['ministerios', 'reparticiones', 'resoluciones'].sort()
    },
    {
        label: 'Organismos', icon: <BusinessIcon fontSize="small" />,
        items: ['modulos', 'oficinas', 'organismos'].sort()
    },
    {
        label: 'Otros', icon: <MoreHorizIcon fontSize="small" />,
        items: ['funciones', 'impuestos', 'tipos-archivo'].sort()
    },
    {
        label: 'Pases', icon: <SwapHorizIcon fontSize="small" />,
        items: ['pases-acciones', 'pases-notificadores', 'pases-prioridades'].sort()
    },
    {
        label: 'Personal', icon: <PeopleIcon fontSize="small" />,
        items: ['agrupamientos', 'areas', 'categorias', 'clases', 'condiciones-laborales', 'escalafones', 'estudios', 'funciones-personal', 'licencias-articulos', 'licencias-tipo', 'profesiones', 'sanciones-articulos', 'sanciones-tipo'].sort()
    },
    {
        label: 'Tribunales', icon: <AccountBalanceIcon fontSize="small" />,
        items: ['fueros', 'jurisdicciones', 'juzgados'].sort()
    },
];

function catalogoLabel(key) {
    return titleCase(key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
}

// Catálogos "virtuales" que no son CRUD genérico (singletons editables como un form).
// Aparecen habilitados en la sidebar pero el right pane usa un editor custom.
const SINGLETON_EDITORS = new Set(['parametros-carga-masiva', 'feriados', 'feria-judicial']);

// Mapa de campos FK → catálogo fuente para dropdowns
const FK_FIELDS = {
    'Id_Jurisdiccion': { catalogo: 'jurisdicciones', labelField: 'Jurisdiccion' },
    'Id_Fuero_Competencia': { catalogo: 'fueros', labelField: 'Fuero_Competencia' },
    'Id_Modulo': { catalogo: 'modulos', labelField: 'Modulo' },
    'Id_Entorno': { options: [{ Id: '1', label: 'Producción' }, { Id: '2', label: 'Test' }] },
    'Tipo_Expediente': { options: [{ Id: 'J', label: 'Judicial' }, { Id: 'A', label: 'Administrativo' }] },
    'Tipo_Movimiento': { options: [{ Id: 'E', label: 'Escrito' }, { Id: 'P', label: 'Providencia' }, { Id: 'S', label: 'Sentencia' }, { Id: 'O', label: 'Otro' }] },
    'Restringido': { options: [{ Id: '0', label: 'No' }, { Id: '1', label: 'Sí' }] },
    'Id_Provincia': { catalogo: 'provincias', labelField: 'Provincia' },
    'Id_Ministerio': { catalogo: 'ministerios', labelField: 'Ministerio' },
    'Id_Escalafon': { catalogo: 'escalafones', labelField: 'Escalafon' },
    'Id_Tipo_Licencia': { catalogo: 'licencias-tipo', labelField: 'Tipo' },
    'Id_Tipo_Sancion': { catalogo: 'sanciones-tipo', labelField: 'Tipo' },
    'Id_Juzgado': { catalogo: 'juzgados', labelField: 'Juzgado' },
    'Id_Departamento': { catalogo: 'codigos-postales-departamentos', labelField: 'Departamento' },
};

function CatalogosAdmin({ showSnack }) {
    const [disponibles, setDisponibles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [globalSearch, setGlobalSearch] = useState('');
    const [selectedKey, setSelectedKey] = useState(null);
    const [datos, setDatos] = useState([]);
    const [loadingDatos, setLoadingDatos] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('add'); // 'add' | 'edit'
    const [editRow, setEditRow] = useState({});
    const [saving, setSaving] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [fkCache, setFkCache] = useState({}); // cache de datos FK
    const [catSearch, setCatSearch] = useState('');
    const catSort = useSort();

    // Load available catalogues
    useEffect(() => {
        (async () => {
            try {
                const r = await jwtFetch('admin/catalogos/disponibles');
                if (r.ok) setDisponibles(r.catalogos || []);
            } catch (e) {
                showSnack('Error cargando catálogos: ' + e.message, 'error');
            } finally {
                setLoading(false);
            }
        })();
    }, [showSnack]);

    const catalogoMeta = useMemo(() => {
        if (!selectedKey) return null;
        return disponibles.find(c => c.key === selectedKey) || null;
    }, [selectedKey, disponibles]);

    // Load catalogue data when selected
    const loadDatos = useCallback(async (key) => {
        setLoadingDatos(true);
        try {
            const r = await jwtFetch(`admin/catalogos/${key}`);
            if (r.ok) setDatos(r.datos || []);
            else showSnack(r.error || 'Error cargando datos', 'error');
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        } finally {
            setLoadingDatos(false);
        }
    }, [showSnack]);

    // Cargar datos FK cuando se necesiten
    const loadFkData = useCallback(async (campos) => {
        const toLoad = campos.filter(c => FK_FIELDS[c]?.catalogo && !fkCache[FK_FIELDS[c].catalogo]);
        for (const c of toLoad) {
            const fk = FK_FIELDS[c];
            try {
                const r = await jwtFetch(`admin/catalogos/${fk.catalogo}`);
                if (r.ok) setFkCache(prev => ({ ...prev, [fk.catalogo]: r.datos || [] }));
            } catch { /* silenciar */ }
        }
    }, [fkCache]);

    const handleSelectCatalogo = useCallback((key) => {
        setSelectedKey(key);
        setDatos([]);
        setCatSearch('');
        // Singletons (editores tipo form) no usan el endpoint genérico
        if (SINGLETON_EDITORS.has(key)) return;
        loadDatos(key);
        // Pre-cargar datos FK si el catálogo los necesita
        const meta = disponibles.find(c => c.key === key);
        if (meta) loadFkData(meta.campos || []);
    }, [loadDatos, disponibles, loadFkData]);

    const handleToggleGroup = (label) => {
        setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
    };

    // Add / Edit dialog
    const openAdd = () => {
        if (!catalogoMeta) return;
        const emptyRow = {};
        (catalogoMeta.campos || []).forEach(c => { emptyRow[c] = ''; });
        setEditRow(emptyRow);
        setDialogMode('add');
        setDialogOpen(true);
        loadFkData(catalogoMeta.campos || []);
    };

    const openEdit = (row) => {
        setEditRow({ ...row });
        setDialogMode('edit');
        setDialogOpen(true);
        if (catalogoMeta) loadFkData(catalogoMeta.campos || []);
    };

    const handleSave = async () => {
        if (!catalogoMeta) return;
        setSaving(true);
        try {
            const body = {};
            (catalogoMeta.campos || []).forEach(c => { body[c] = editRow[c] ?? ''; });

            let r;
            if (dialogMode === 'add') {
                r = await jwtFetch(`admin/catalogos/${selectedKey}`, {
                    method: 'POST', body: JSON.stringify(body)
                });
            } else {
                const pk = catalogoMeta.pk;
                const id = editRow[pk];
                r = await jwtFetch(`admin/catalogos/${selectedKey}/${id}`, {
                    method: 'PUT', body: JSON.stringify(body)
                });
            }
            if (r.ok) {
                showSnack(dialogMode === 'add' ? 'Registro creado' : 'Registro actualizado', 'success');
                setDialogOpen(false);
                loadDatos(selectedKey);
            } else {
                showSnack(r.mensaje || r.error || 'Error guardando', 'error');
            }
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    // Delete
    const confirmDelete = (row) => {
        setDeleteTarget(row);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (!catalogoMeta || !deleteTarget) return;
        setSaving(true);
        try {
            const pk = catalogoMeta.pk;
            const id = deleteTarget[pk];
            const r = await jwtFetch(`admin/catalogos/${selectedKey}/${id}`, { method: 'DELETE' });
            if (r.ok) {
                showSnack('Registro eliminado', 'success');
                setDeleteConfirmOpen(false);
                setDeleteTarget(null);
                loadDatos(selectedKey);
            } else {
                showSnack(r.mensaje || r.error || 'Error eliminando', 'error');
            }
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const availableKeys = useMemo(() => {
        const s = new Set((disponibles || []).map(c => c.key));
        SINGLETON_EDITORS.forEach(k => s.add(k));
        return s;
    }, [disponibles]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: PRIMARY }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', height: '100%', gap: 2 }}>
            {/* Left: catalogue groups sidebar */}
            <Paper sx={{
                width: 250, minWidth: 250, borderRadius: BORDER_RADIUS, overflow: 'hidden',
                border: '1px solid #e0e0e0', flexShrink: 0, display: 'flex', flexDirection: 'column'
            }}>
                <Box sx={{
                    px: 2, py: 1.5, bgcolor: PRIMARY_LIGHT,
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1 }}>
                        Catálogos del sistema
                    </Typography>
                    <TextField size="small" fullWidth placeholder="Buscar catálogo..."
                        value={globalSearch} onChange={e => setGlobalSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Escape') setGlobalSearch(''); }}
                        InputProps={{ sx: { fontSize: '0.8rem', bgcolor: '#fff', borderRadius: BORDER_RADIUS } }}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY } }}
                    />
                </Box>
                <List dense sx={{ py: 0, flex: 1, overflowY: 'auto' }}>
                    {(() => {
                        const q = globalSearch.toLowerCase();
                        // Si hay búsqueda: mostrar flat list filtrada con grupo
                        if (q) {
                            const results = [];
                            CATALOGO_GROUPS.forEach(group => {
                                group.items.filter(k => availableKeys.has(k) && catalogoLabel(k).toLowerCase().includes(q))
                                    .forEach(key => results.push({ key, group: group.label }));
                            });
                            if (results.length === 0) return (
                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ color: '#999' }}>Sin resultados</Typography>
                                </Box>
                            );
                            return results.map(({ key, group }) => (
                                <ListItemButton key={key} selected={selectedKey === key}
                                    onClick={() => handleSelectCatalogo(key)}
                                    sx={{ py: 0.5, px: 2,
                                        '&.Mui-selected': { bgcolor: PRIMARY_LIGHT, borderRight: `3px solid ${PRIMARY}`, '&:hover': { bgcolor: PRIMARY_LIGHT } },
                                        '&:hover': { bgcolor: '#fafafa' } }}>
                                    <ListItemText
                                        primary={catalogoLabel(key)}
                                        secondary={group}
                                        primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: selectedKey === key ? 700 : 400 }}
                                        secondaryTypographyProps={{ fontSize: '0.65rem', color: '#999' }}
                                    />
                                </ListItemButton>
                            ));
                        }
                        // Sin búsqueda: grupos colapsables
                        return CATALOGO_GROUPS.map(group => {
                        const isOpen = !!expandedGroups[group.label];
                        const groupItems = group.items.filter(k => availableKeys.has(k));
                        return (
                            <React.Fragment key={group.label}>
                                <ListItemButton onClick={() => handleToggleGroup(group.label)}
                                    sx={{ py: 0.75, '&:hover': { bgcolor: PRIMARY_LIGHT } }}>
                                    <ListItemIcon sx={{ minWidth: 32, color: PRIMARY }}>
                                        {group.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={group.label}
                                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
                                    {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </ListItemButton>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <List dense disablePadding>
                                        {groupItems.map(key => (
                                            <ListItemButton key={key}
                                                selected={selectedKey === key}
                                                onClick={() => handleSelectCatalogo(key)}
                                                sx={{
                                                    pl: 5, py: 0.5,
                                                    '&.Mui-selected': {
                                                        bgcolor: PRIMARY_LIGHT,
                                                        borderRight: `3px solid ${PRIMARY}`,
                                                        '&:hover': { bgcolor: PRIMARY_LIGHT }
                                                    },
                                                    '&:hover': { bgcolor: '#fafafa' }
                                                }}>
                                                <ListItemText primary={catalogoLabel(key)}
                                                    primaryTypographyProps={{ fontSize: '0.8rem' }} />
                                            </ListItemButton>
                                        ))}
                                        {group.items.filter(k => !availableKeys.has(k)).map(key => (
                                            <ListItemButton key={key} disabled sx={{ pl: 5, py: 0.5 }}>
                                                <ListItemText primary={catalogoLabel(key)}
                                                    primaryTypographyProps={{ fontSize: '0.8rem', color: '#bbb' }} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    });
                    })()}
                </List>
            </Paper>

            {/* Right: data table */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {!selectedKey ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                        <Typography color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                            Seleccione un catálogo del panel izquierdo
                        </Typography>
                    </Box>
                ) : selectedKey === 'parametros-carga-masiva' ? (
                    <ParametrosCargaMasivaEditor showSnack={showSnack} />
                ) : selectedKey === 'feriados' ? (
                    <FeriadosEditor showSnack={showSnack} />
                ) : selectedKey === 'feria-judicial' ? (
                    <FeriaJudicialEditor showSnack={showSnack} />
                ) : loadingDatos ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: PRIMARY }} />
                    </Box>
                ) : (
                    <>
                        {/* Header con ruta + buscador */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: '#999' }}>
                                {CATALOGO_GROUPS.find(g => g.items.includes(selectedKey))?.label || ''} &gt;
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', fontSize: '1.1rem' }}>
                                {catalogoLabel(selectedKey)}
                            </Typography>
                            <Chip label={`${datos.length} registros`} size="small"
                                sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 600, fontSize: '0.75rem' }} />
                            <Box sx={{ flex: 1 }} />
                            <TextField size="small" placeholder="Buscar..." value={catSearch}
                                onChange={e => setCatSearch(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Escape') setCatSearch(''); }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: BORDER_RADIUS },
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY } }} />
                            <Button variant="contained" size="small" startIcon={<AddIcon />}
                                onClick={openAdd}
                                sx={{
                                    bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                                    '&:hover': { bgcolor: PRIMARY_DARK }
                                }}>
                                Agregar
                            </Button>
                        </Box>

                        <TableContainer component={Paper} sx={{
                            borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0',
                            maxHeight: 'calc(100vh - 250px)'
                        }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {(catalogoMeta?.campos || []).map(col => (
                                            <SortHead key={col} sort={catSort} field={col} sx={{
                                                fontWeight: 700, bgcolor: '#f5f5f5', fontSize: '0.8rem',
                                                borderBottom: `2px solid ${PRIMARY}`, whiteSpace: 'nowrap'
                                            }}>
                                                {titleCase(col.replace(/_/g, ' '))}
                                            </SortHead>
                                        ))}
                                        <TableCell sx={{
                                            fontWeight: 700, bgcolor: '#f5f5f5', fontSize: '0.8rem',
                                            borderBottom: `2px solid ${PRIMARY}`, width: 100, textAlign: 'center'
                                        }}>
                                            Acciones
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        const q = catSearch.toLowerCase();
                                        const filtered = q ? datos.filter(row => (catalogoMeta?.campos || []).some(c => String(row[c] ?? '').toLowerCase().includes(q))) : datos;
                                        const sorted = catSort.sorted(filtered);
                                        if (sorted.length === 0) return (
                                            <TableRow><TableCell colSpan={(catalogoMeta?.campos?.length || 1) + 1}
                                                sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                                                {q ? 'Sin resultados para la búsqueda' : 'No hay registros'}
                                            </TableCell></TableRow>
                                        );
                                        return sorted.map((row, idx) => (
                                        <TableRow key={row[catalogoMeta?.pk] || idx}
                                            hover sx={{
                                                '&:hover': { bgcolor: PRIMARY_LIGHT + '44' },
                                                '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                                            }}>
                                            {(catalogoMeta?.campos || []).map(col => {
                                                let displayVal = String(row[col] ?? '');
                                                const fk = FK_FIELDS[col];
                                                if (fk?.options) {
                                                    const opt = fk.options.find(o => String(o.Id) === displayVal);
                                                    if (opt) displayVal = opt.label;
                                                } else if (fk?.catalogo) {
                                                    const fkData = fkCache[fk.catalogo] || [];
                                                    const found = fkData.find(r => String(r.Id) === displayVal);
                                                    if (found) displayVal = found[fk.labelField] || displayVal;
                                                }
                                                return (
                                                    <TableCell key={col} sx={{ fontSize: '0.82rem', py: 0.75 }}>
                                                        {titleCase(displayVal)}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                                                <Tooltip title="Editar">
                                                    <IconButton size="small" onClick={() => openEdit(row)}
                                                        sx={{ color: PRIMARY }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Eliminar">
                                                    <IconButton size="small" onClick={() => confirmDelete(row)}
                                                        sx={{ color: '#d32f2f' }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ));
                                    })()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Box>

            {/* Add/Edit Dialog */}
            <ColorDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title={dialogMode === 'add' ? 'Agregar registro' : 'Editar registro'}
                icon={dialogMode === 'add' ? <AddIcon /> : <EditIcon />}
                actions={
                    <>
                        <Button onClick={() => setDialogOpen(false)} sx={{ color: '#666', textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            sx={{
                                bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                                '&:hover': { bgcolor: PRIMARY_DARK }
                            }}>
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </>
                }>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {(catalogoMeta?.campos || []).map(campo => {
                        const isPk = campo === catalogoMeta?.pk;
                        const fk = FK_FIELDS[campo];
                        const inputSx = {
                            '& .MuiOutlinedInput-root': { borderRadius: BORDER_RADIUS },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY }
                        };

                        // Campo FK con opciones estáticas
                        if (fk?.options) {
                            return (
                                <TextField key={campo} label={titleCase(campo.replace(/_/g, ' '))}
                                    value={editRow[campo] ?? ''} select size="small" fullWidth
                                    onChange={(e) => setEditRow(prev => ({ ...prev, [campo]: e.target.value }))}
                                    SelectProps={{ native: true }} sx={inputSx}>
                                    <option value="">— Seleccionar —</option>
                                    {fk.options.map(o => <option key={o.Id} value={o.Id}>{o.label}</option>)}
                                </TextField>
                            );
                        }
                        // Campo FK con catálogo dinámico
                        if (fk?.catalogo) {
                            const fkData = fkCache[fk.catalogo] || [];
                            return (
                                <TextField key={campo} label={titleCase(campo.replace(/_/g, ' '))}
                                    value={editRow[campo] ?? ''} select size="small" fullWidth
                                    onChange={(e) => setEditRow(prev => ({ ...prev, [campo]: e.target.value }))}
                                    SelectProps={{ native: true }} sx={inputSx}>
                                    <option value="">— Seleccionar —</option>
                                    {fkData.map(row => (
                                        <option key={row.Id} value={row.Id}>{row[fk.labelField] || row.Id}</option>
                                    ))}
                                </TextField>
                            );
                        }
                        // Campo normal
                        return (
                            <TextField key={campo} label={titleCase(campo.replace(/_/g, ' '))}
                                value={editRow[campo] ?? ''}
                                onChange={(e) => setEditRow(prev => ({ ...prev, [campo]: e.target.value }))}
                                disabled={isPk && dialogMode === 'edit'}
                                size="small" fullWidth sx={inputSx} />
                        );
                    })}
                </Box>
            </ColorDialog>

            {/* Delete Confirmation Dialog */}
            <ColorDialog
                open={deleteConfirmOpen}
                onClose={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}
                title="Confirmar eliminación"
                icon={<DeleteIcon />}
                actions={
                    <>
                        <Button onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}
                            sx={{ color: '#666', textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={handleDelete} disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                            sx={{
                                bgcolor: '#d32f2f', borderRadius: BORDER_RADIUS, textTransform: 'none',
                                '&:hover': { bgcolor: '#b71c1c' }
                            }}>
                            {saving ? 'Eliminando...' : 'Eliminar'}
                        </Button>
                    </>
                }>
                <Typography sx={{ py: 1 }}>
                    ¿Está seguro de que desea eliminar este registro? Esta acción no se puede deshacer.
                </Typography>
            </ColorDialog>
        </Box>
    );
}

// ═══════════════════════════════════════════════════════════
// EDITOR SINGLETON: Parámetros Carga Masiva
// 3 secciones: configuración general, umbrales de relevancia, selector de abogados.
// ═══════════════════════════════════════════════════════════
function ParametrosCargaMasivaEditor({ showSnack }) {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [funciones, setFunciones] = useState([]); // catálogo Funciones (personal)
    const [areas, setAreas] = useState([]);         // catálogo Areas

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, fr, ar] = await Promise.all([
                jwtFetch('admin/catalogos/sistema/parametros-carga-masiva'),
                jwtFetch('admin/catalogos/funciones-personal'),
                jwtFetch('admin/catalogos/areas'),
            ]);
            if (r.ok) setData(r.parametros || {});
            if (fr.ok) setFunciones((fr.datos || []).filter(x => x.Funcion && x.Funcion.trim()));
            if (ar.ok) setAreas((ar.datos || []).filter(x => x.Area && x.Area.trim()));
        } catch (e) {
            showSnack('Error cargando parámetros: ' + e.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnack]);

    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async () => {
        setSaving(true);
        try {
            const r = await jwtFetch('admin/catalogos/sistema/parametros-carga-masiva', {
                method: 'POST', body: JSON.stringify(data)
            });
            if (r.ok) showSnack('Parámetros de Carga Masiva guardados', 'success');
            else showSnack(r.mensaje || r.error || 'Error guardando', 'error');
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const setField = (key, value) => setData(prev => ({ ...prev, [key]: value }));

    const NumField = ({ keyName, label, hint }) => (
        <TextField
            label={label}
            helperText={hint || ' '}
            type="number"
            value={data[keyName] ?? ''}
            onChange={(e) => setField(keyName, e.target.value)}
            size="small" fullWidth
            sx={{
                '& .MuiOutlinedInput-root': { borderRadius: BORDER_RADIUS },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY }
            }}
        />
    );

    const SelectFK = ({ keyName, label, hint, opciones, labelField }) => {
        const valStr = data[keyName] !== undefined && data[keyName] !== null ? String(data[keyName]) : '';
        const selected = opciones.find(o => String(o.Id) === valStr) || null;
        return (
            <Autocomplete
                size="small"
                options={opciones}
                value={selected}
                onChange={(_, v) => setField(keyName, v ? v.Id : '')}
                getOptionLabel={(o) => o ? `${o[labelField]} (Id ${o.Id})` : ''}
                isOptionEqualToValue={(a, b) => String(a?.Id) === String(b?.Id)}
                renderInput={(params) => (
                    <TextField {...params} label={label} helperText={hint || ' '}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: BORDER_RADIUS },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY }
                        }} />
                )}
                fullWidth
            />
        );
    };

    const Section = ({ icon, title, subtitle, children }) => (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: BORDER_RADIUS, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {icon}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>{title}</Typography>
            </Box>
            {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
        </Paper>
    );

    return (
        <Paper sx={{ p: 3, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#999' }}>Carga Masiva &gt;</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', fontSize: '1.1rem' }}>
                    Parámetros Carga Masiva
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configuración global del módulo. Estos valores impactan en el ruteo de causas al PJ y en la asignación
                automática de abogados a los lotes.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: PRIMARY }} />
                </Box>
            ) : (
                <>
                    <Section icon={<SettingsIcon fontSize="small" sx={{ color: PRIMARY }} />}
                        title="Configuración general"
                        subtitle="Reglas globales de ruteo y alertas operativas.">
                        <NumField keyName="Monto_Tope_Justicia_Paz"
                            label="Monto tope Justicia de Paz ($)"
                            hint="Si la causa tiene monto MENOR a este valor y el CP del demandado está en la lista 'CP Justicia Paz', NO va al Poder Judicial." />
                        <NumField keyName="Dias_Alerta_Causas_Sin_Movimiento"
                            label="Días de alerta para causas sin movimiento"
                            hint="Cantidad de días sin movimientos antes de marcar el expediente con alerta." />
                    </Section>

                    <Section icon={<AccountBalanceIcon fontSize="small" sx={{ color: PRIMARY }} />}
                        title="Umbrales de Relevancia Económica"
                        subtitle="Clasifican el riesgo de un expediente según el monto demandado.">
                        <NumField keyName="Relevancia_Economica_AM"
                            label="Umbral Alta / Media ($)"
                            hint="Montos IGUALES o MAYORES a este valor se clasifican como Alta relevancia." />
                        <NumField keyName="Relevancia_Economica_MB"
                            label="Umbral Media / Baja ($)"
                            hint="Montos entre este valor y el umbral Alta/Media son Media; por debajo, Baja." />
                    </Section>

                    <Section icon={<PeopleIcon fontSize="small" sx={{ color: PRIMARY }} />}
                        title="Selector de Abogados"
                        subtitle="Define qué personal está habilitado para sortear/asignar a un lote: el sistema busca a quienes tengan ALGUNO de los roles listados, dentro de ALGUNA de las áreas configuradas.">
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: PRIMARY, display: 'block', mb: 1, mt: 0.5 }}>
                                ROLES JERÁRQUICOS HABILITADOS
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectFK keyName="Id_Funcion_Selector_Abogados"
                                    label="Rol principal"
                                    hint="Función jerárquica que recibe la asignación principal (ej. Director de Área)."
                                    opciones={funciones} labelField="Funcion" />
                                <SelectFK keyName="Id_Funcion2_Selector_Abogados"
                                    label="Rol adicional 2"
                                    hint="Otro rol elegible (ej. Sub-Director de Área)."
                                    opciones={funciones} labelField="Funcion" />
                                <SelectFK keyName="Id_Funcion3_Selector_Abogados"
                                    label="Rol adicional 3"
                                    hint="Otro rol elegible (ej. Consejo Asesor)."
                                    opciones={funciones} labelField="Funcion" />
                                <SelectFK keyName="Id_Funcion4_Selector_Abogados"
                                    label="Rol adicional 4"
                                    hint="Otro rol elegible (ej. Miembro del Cuerpo de Abogados)."
                                    opciones={funciones} labelField="Funcion" />
                            </Box>
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: PRIMARY, display: 'block', mb: 1, mt: 0.5 }}>
                                ÁREAS HABILITADAS
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectFK keyName="Id_Area_Selector_Abogados"
                                    label="Área principal"
                                    hint="Área para causas tributarias (Id_Modulo=1). Ej: Ejecuciones Tributarias."
                                    opciones={areas} labelField="Area" />
                                <SelectFK keyName="Id_Area2_Selector_Abogados"
                                    label="Área secundaria"
                                    hint="Área para causas no tributarias (Id_Modulo=2). Ej: Acciones Residual BSJ."
                                    opciones={areas} labelField="Area" />
                            </Box>
                        </Box>
                    </Section>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button variant="contained" startIcon={
                            saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                        }
                            onClick={guardar} disabled={saving}
                            sx={{
                                bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                                '&:hover': { bgcolor: PRIMARY_DARK }
                            }}>
                            {saving ? 'Guardando...' : 'Guardar parámetros'}
                        </Button>
                    </Box>
                </>
            )}
        </Paper>
    );
}

// ═══════════════════════════════════════════════════════════
// EDITOR SINGLETON: Feriados (CRUD + import API)
// ═══════════════════════════════════════════════════════════
function FeriadosEditor({ showSnack }) {
    const [ano, setAno] = useState(new Date().getFullYear());
    const [feriados, setFeriados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [nuevo, setNuevo] = useState({ Fecha: '', Motivo: '', Observaciones: '' });
    const [savingNuevo, setSavingNuevo] = useState(false);

    const cargar = useCallback(async (a) => {
        setLoading(true);
        try {
            const r = await jwtFetch(`admin/catalogos/sistema/feriados?ano=${a}`);
            if (r.ok) setFeriados(r.feriados || []);
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        finally { setLoading(false); }
    }, [showSnack]);

    useEffect(() => { cargar(ano); }, [cargar, ano]);

    const importar = async () => {
        setImporting(true);
        try {
            const r = await jwtFetch('admin/catalogos/sistema/feriados/importar', {
                method: 'POST', body: JSON.stringify({ ano })
            });
            if (r.ok) { showSnack(`${r.importados} feriados importados`, 'success'); cargar(ano); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        finally { setImporting(false); }
    };

    const eliminar = async (fecha) => {
        try {
            const r = await jwtFetch(`admin/catalogos/sistema/feriados/${fecha}`, { method: 'DELETE' });
            if (r.ok) { showSnack('Feriado eliminado'); cargar(ano); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
    };

    const guardarNuevo = async () => {
        if (!nuevo.Fecha || !nuevo.Motivo) { showSnack('Fecha y Motivo son obligatorios', 'warning'); return; }
        setSavingNuevo(true);
        try {
            const r = await jwtFetch('admin/catalogos/sistema/feriados', {
                method: 'POST',
                body: JSON.stringify({ ...nuevo, Ano: parseInt(nuevo.Fecha.substring(0, 4)) }),
            });
            if (r.ok) {
                showSnack('Feriado creado', 'success');
                setAddOpen(false);
                setNuevo({ Fecha: '', Motivo: '', Observaciones: '' });
                cargar(ano);
            } else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        finally { setSavingNuevo(false); }
    };

    return (
        <Paper sx={{ p: 3, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#999' }}>Calendario &gt;</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', fontSize: '1.1rem' }}>Feriados</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Días no laborables del año. Se pueden cargar automáticamente desde la API pública de feriados argentinos.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <TextField label="Año" type="number" size="small" value={ano}
                    onChange={e => setAno(parseInt(e.target.value) || new Date().getFullYear())}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 100, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY } }} />
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}
                    sx={{ borderColor: PRIMARY, color: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS,
                        '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY } }}>
                    Agregar
                </Button>
                <Button variant="contained" size="small" onClick={importar} disabled={importing}
                    startIcon={importing ? <CircularProgress size={14} color="inherit" /> : <CalendarMonthIcon />}
                    sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                    {importing ? 'Importando...' : `Importar ${ano} desde API`}
                </Button>
                <Chip label={`${feriados.length} feriados`} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 600 }} />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
            ) : (
                <TableContainer sx={{ borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0', maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Motivo</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}`, width: 60, textAlign: 'center' }}>Acc.</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feriados.length === 0 ? (
                                <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: '#999' }}>
                                    No hay feriados para {ano}. Use "Importar" para cargarlos automáticamente.
                                </TableCell></TableRow>
                            ) : feriados.map((f, i) => {
                                const fechaStr = (f.Fecha || '').substring(0, 10);
                                const [yy, mm, dd] = fechaStr.split('-').map(Number);
                                const fecha = fechaStr ? new Date(yy, mm - 1, dd) : null;
                                const fmtF = fecha ? fecha.toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long' }) : '-';
                                return (
                                    <TableRow key={f.Fecha || i} hover>
                                        <TableCell sx={{ fontSize: '0.82rem', textTransform: 'capitalize' }}>{fmtF}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{f.Motivo}</TableCell>
                                        <TableCell sx={{ fontSize: '0.82rem' }}>
                                            <Chip label={f.Observaciones || 'feriado'} size="small"
                                                sx={{ fontSize: '0.7rem', borderRadius: BORDER_RADIUS,
                                                    bgcolor: f.Observaciones === 'inamovible' ? '#e8f5e9' : f.Observaciones === 'trasladable' ? '#fff3e0' : '#e3f2fd',
                                                    color: f.Observaciones === 'inamovible' ? '#2e7d32' : f.Observaciones === 'trasladable' ? '#e65100' : '#1565c0' }} />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                                            <IconButton size="small" onClick={() => eliminar(f.Fecha?.substring(0, 10))} sx={{ color: '#d32f2f' }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ColorDialog open={addOpen} onClose={() => setAddOpen(false)}
                title="Nuevo feriado" icon={<CalendarMonthIcon fontSize="small" />}
                actions={
                    <>
                        <Button onClick={() => setAddOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
                        <Button onClick={guardarNuevo} disabled={savingNuevo} variant="contained"
                            startIcon={savingNuevo ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            sx={{ bgcolor: PRIMARY, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>
                            Guardar
                        </Button>
                    </>
                }>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                    <TextField label="Fecha" type="date" size="small" InputLabelProps={{ shrink: true }}
                        value={nuevo.Fecha} onChange={e => setNuevo(p => ({ ...p, Fecha: e.target.value }))} />
                    <TextField label="Motivo" size="small" value={nuevo.Motivo}
                        onChange={e => setNuevo(p => ({ ...p, Motivo: e.target.value }))} />
                    <TextField label="Tipo (opcional)" size="small" placeholder="inamovible / trasladable / provincial..."
                        value={nuevo.Observaciones}
                        onChange={e => setNuevo(p => ({ ...p, Observaciones: e.target.value }))} />
                </Box>
            </ColorDialog>
        </Paper>
    );
}

// ═══════════════════════════════════════════════════════════
// EDITOR SINGLETON: Feria Judicial (períodos con horario reducido)
// Usa los endpoints biometrico/ferias y biometrico/horarios.
// ═══════════════════════════════════════════════════════════
function FeriaJudicialEditor({ showSnack }) {
    const [ferias, setFerias] = useState([]);
    const [horarios, setHorarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dlg, setDlg] = useState({ open: false, Nombre: '', Fecha_Desde: '', Fecha_Hasta: '', Id_Horario: '', Ano: new Date().getFullYear(), saving: false });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [fr, hr] = await Promise.all([
                jwtFetch('biometrico/ferias'),
                jwtFetch('biometrico/horarios'),
            ]);
            if (fr.ok) setFerias(fr.ferias || []);
            if (hr.ok) setHorarios(hr.horarios || []);
        } catch (e) {
            showSnack('Error cargando: ' + e.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnack]);

    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async () => {
        if (!dlg.Nombre || !dlg.Fecha_Desde || !dlg.Fecha_Hasta || !dlg.Id_Horario) {
            showSnack('Complete todos los campos', 'warning'); return;
        }
        setDlg(p => ({ ...p, saving: true }));
        try {
            const r = await jwtFetch('biometrico/ferias', {
                method: 'POST',
                body: JSON.stringify({
                    Nombre: dlg.Nombre, Fecha_Desde: dlg.Fecha_Desde, Fecha_Hasta: dlg.Fecha_Hasta,
                    Id_Horario: dlg.Id_Horario, Ano: dlg.Ano,
                }),
            });
            if (r.ok) {
                showSnack('Feria guardada', 'success');
                setDlg({ open: false, Nombre: '', Fecha_Desde: '', Fecha_Hasta: '', Id_Horario: '', Ano: new Date().getFullYear(), saving: false });
                cargar();
            } else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        finally { setDlg(p => ({ ...p, saving: false })); }
    };

    const eliminar = async (id) => {
        try {
            const r = await jwtFetch(`biometrico/ferias/${id}`, { method: 'DELETE' });
            if (r.ok) { showSnack('Feria eliminada'); cargar(); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
    };

    const horarioNombre = (id) => {
        const h = horarios.find(h => String(h.Id) === String(id));
        return h ? (h.Nombre || h.Descripcion || `#${h.Id}`) : `#${id}`;
    };

    const fmtFecha = (f) => {
        const s = (f || '').substring(0, 10);
        if (!s) return '-';
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString('es-AR');
    };

    return (
        <Paper sx={{ p: 3, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#999' }}>Calendario &gt;</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', fontSize: '1.1rem' }}>Feria Judicial</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Períodos del calendario judicial (feria de invierno, feria de verano, etc.) con un horario especial asociado.
                Estos rangos los usa el módulo Biométrico para calcular la asistencia del personal con horario reducido.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <Button variant="contained" size="small" startIcon={<AddIcon />}
                    onClick={() => setDlg(p => ({ ...p, open: true }))}
                    sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                    Agregar feria
                </Button>
                <Chip label={`${ferias.length} ferias`} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 600 }} />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
            ) : (
                <TableContainer sx={{ borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Nombre</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Desde</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Hasta</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Horario aplicado</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}` }}>Año</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}`, width: 60, textAlign: 'center' }}>Acc.</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ferias.length === 0 ? (
                                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: '#999' }}>
                                    No hay ferias judiciales cargadas.
                                </TableCell></TableRow>
                            ) : ferias.map(f => (
                                <TableRow key={f.Id} hover>
                                    <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{f.Nombre}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem' }}>{fmtFecha(f.Fecha_Desde)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem' }}>{fmtFecha(f.Fecha_Hasta)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem' }}>{horarioNombre(f.Id_Horario)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem' }}>{f.Ano}</TableCell>
                                    <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                                        <IconButton size="small" onClick={() => eliminar(f.Id)} sx={{ color: '#d32f2f' }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ColorDialog open={dlg.open} onClose={() => setDlg(p => ({ ...p, open: false }))}
                title="Nueva feria judicial" icon={<CalendarMonthIcon fontSize="small" />}
                actions={
                    <>
                        <Button onClick={() => setDlg(p => ({ ...p, open: false }))} sx={{ textTransform: 'none' }}>Cancelar</Button>
                        <Button onClick={guardar} disabled={dlg.saving} variant="contained"
                            startIcon={dlg.saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            sx={{ bgcolor: PRIMARY, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>
                            Guardar
                        </Button>
                    </>
                }>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                    <TextField label="Nombre" size="small" value={dlg.Nombre}
                        placeholder="Ej: Feria de Invierno"
                        onChange={e => setDlg(p => ({ ...p, Nombre: e.target.value }))} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField label="Desde" type="date" size="small" InputLabelProps={{ shrink: true }} fullWidth
                            value={dlg.Fecha_Desde} onChange={e => setDlg(p => ({ ...p, Fecha_Desde: e.target.value }))} />
                        <TextField label="Hasta" type="date" size="small" InputLabelProps={{ shrink: true }} fullWidth
                            value={dlg.Fecha_Hasta} onChange={e => setDlg(p => ({ ...p, Fecha_Hasta: e.target.value }))} />
                    </Box>
                    <Autocomplete size="small"
                        options={horarios}
                        value={horarios.find(h => String(h.Id) === String(dlg.Id_Horario)) || null}
                        onChange={(_, v) => setDlg(p => ({ ...p, Id_Horario: v ? v.Id : '' }))}
                        getOptionLabel={(o) => o ? (o.Nombre || o.Descripcion || `Horario #${o.Id}`) : ''}
                        isOptionEqualToValue={(a, b) => String(a?.Id) === String(b?.Id)}
                        renderInput={(params) => <TextField {...params} label="Horario aplicado" />} />
                    <TextField label="Año" type="number" size="small" value={dlg.Ano}
                        onChange={e => setDlg(p => ({ ...p, Ano: parseInt(e.target.value) || new Date().getFullYear() }))} />
                </Box>
            </ColorDialog>
        </Paper>
    );
}

// ═══════════════════════════════════════════════════════════
// SISTEMA CONFIG SECTION
// ═══════════════════════════════════════════════════════════
function SistemaConfig({ showSnack }) {
    const [activeSection, setActiveSection] = useState('reglas');
    // Banners state (multi-banner)
    const [banners, setBanners] = useState([]);
    const [loadingMensaje, setLoadingMensaje] = useState(true);
    const [bannerEditing, setBannerEditing] = useState(null);   // null = no dialog. {} = new. {Id, ...} = edit
    const [savingBanner, setSavingBanner] = useState(false);
    const [confirmDeleteBanner, setConfirmDeleteBanner] = useState(null);
    // Reglas state
    const [reglas, setReglas] = useState([]);
    const [aplicadas, setAplicadas] = useState([]);
    const [reglasGrupos, setReglasGrupos] = useState([]);
    const [reglasModulos, setReglasModulos] = useState([]);
    const [loadingReglas, setLoadingReglas] = useState(false);
    const [selectedModulo, setSelectedModulo] = useState(null);
    const [matrizReglas, setMatrizReglas] = useState({});  // { "grupoId_reglaId": 0|1 }
    const [savingReglas, setSavingReglas] = useState(false);
    const [matrizDirty, setMatrizDirty] = useState(false);
    // Parametros state
    const [parametros, setParametros] = useState({});
    const [loadingParametros, setLoadingParametros] = useState(false);
    const [savingParametros, setSavingParametros] = useState(false);

    // Load banners
    const cargarBanners = useCallback(async () => {
        try {
            const r = await jwtFetch('admin/catalogos/sistema/banners/admin');
            if (r.ok) setBanners(r.items || []);
        } catch (e) {
            showSnack('Error cargando banners: ' + e.message, 'error');
        } finally {
            setLoadingMensaje(false);
        }
    }, [showSnack]);
    useEffect(() => { cargarBanners(); }, [cargarBanners]);

    const handleGuardarBanner = async () => {
        if (!bannerEditing) return;
        setSavingBanner(true);
        try {
            const body = {
                Titulo: bannerEditing.Titulo || 'SIGIF',
                Mensaje: bannerEditing.Mensaje || '',
                Cuerpo_Detalle: bannerEditing.Cuerpo_Detalle || null,
                Background_Color: bannerEditing.Background_Color || '#1769aa',
                Visible: !!bannerEditing.Visible,
                Orden: Number(bannerEditing.Orden) || 0,
            };
            let r;
            if (bannerEditing.Id) {
                r = await jwtFetch(`admin/catalogos/sistema/banners/${bannerEditing.Id}`, {
                    method: 'PUT', body: JSON.stringify(body),
                });
            } else {
                r = await jwtFetch('admin/catalogos/sistema/banners', {
                    method: 'POST', body: JSON.stringify(body),
                });
            }
            if (r?.ok) {
                showSnack('Banner guardado', 'success');
                setBannerEditing(null);
                cargarBanners();
            } else {
                showSnack(r?.mensaje || 'Error al guardar', 'error');
            }
        } catch (e) {
            showSnack('Error al guardar: ' + e.message, 'error');
        } finally {
            setSavingBanner(false);
        }
    };

    const handleToggleBanner = async (banner) => {
        try {
            const r = await jwtFetch(`admin/catalogos/sistema/banners/${banner.Id}/toggle`, {
                method: 'POST', body: JSON.stringify({ visible: !banner.Visible }),
            });
            if (r?.ok) cargarBanners();
            else showSnack(r?.mensaje || 'Error', 'error');
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        }
    };

    const handleEliminarBanner = async () => {
        if (!confirmDeleteBanner) return;
        try {
            const r = await jwtFetch(`admin/catalogos/sistema/banners/${confirmDeleteBanner.Id}`, { method: 'DELETE' });
            if (r?.ok) {
                showSnack('Banner eliminado', 'success');
                setConfirmDeleteBanner(null);
                cargarBanners();
            } else {
                showSnack(r?.mensaje || 'Error', 'error');
            }
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        }
    };

    // Load reglas
    const loadReglas = useCallback(async () => {
        setLoadingReglas(true);
        try {
            const r = await jwtFetch('admin/catalogos/sistema/reglas');
            if (r.ok) {
                setReglas(r.reglas || []);
                setAplicadas(r.aplicadas || []);
                setReglasGrupos(r.grupos || []);
                setReglasModulos(r.modulos || []);
            }
        } catch (e) {
            showSnack('Error cargando reglas: ' + e.message, 'error');
        } finally {
            setLoadingReglas(false);
        }
    }, [showSnack]);

    // Cuando se selecciona un módulo, armar la matriz
    useEffect(() => {
        if (!selectedModulo) { setMatrizReglas({}); setMatrizDirty(false); return; }
        const m = {};
        // Inicializar todo en 0
        reglasGrupos.forEach(g => reglas.forEach(r => { m[`${g.Id}_${r.Id}`] = 0; }));
        // Cargar los que ya están aplicados para este módulo
        aplicadas.filter(a => String(a.Id_Modulo) === String(selectedModulo.Id))
            .forEach(a => { m[`${a.Id_Grupo}_${a.Id_Regla}`] = a.Accion ?? 0; });
        setMatrizReglas(m);
        setMatrizDirty(false);
    }, [selectedModulo, aplicadas, reglasGrupos, reglas]);

    const toggleRegla = (grupoId, reglaId) => {
        setMatrizReglas(prev => ({
            ...prev,
            [`${grupoId}_${reglaId}`]: prev[`${grupoId}_${reglaId}`] ? 0 : 1
        }));
        setMatrizDirty(true);
    };

    const handleSaveReglas = async () => {
        if (!selectedModulo) return;
        setSavingReglas(true);
        try {
            const asignaciones = [];
            reglasGrupos.forEach(g => reglas.forEach(r => {
                asignaciones.push({ idGrupo: g.Id, idRegla: r.Id, accion: matrizReglas[`${g.Id}_${r.Id}`] || 0 });
            }));
            const res = await jwtFetch('admin/catalogos/sistema/reglas', {
                method: 'POST', body: JSON.stringify({ idModulo: selectedModulo.Id, asignaciones })
            });
            if (res.ok) {
                showSnack('Reglas guardadas correctamente', 'success');
                setMatrizDirty(false);
                loadReglas(); // Recargar
            } else showSnack('Error guardando reglas', 'error');
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        } finally {
            setSavingReglas(false);
        }
    };

    // Load parametros
    const loadParametros = useCallback(async () => {
        setLoadingParametros(true);
        try {
            const r = await jwtFetch('admin/catalogos/sistema/parametros');
            if (r.ok) setParametros(r.parametros || {});
        } catch (e) {
            showSnack('Error cargando parámetros: ' + e.message, 'error');
        } finally {
            setLoadingParametros(false);
        }
    }, [showSnack]);

    useEffect(() => {
        if (activeSection === 'reglas') loadReglas();
        if (activeSection === 'parametros') loadParametros();
    }, [activeSection, loadReglas, loadParametros]); // eslint-disable-line

    const handleSaveParametros = async () => {
        setSavingParametros(true);
        try {
            const r = await jwtFetch('admin/catalogos/sistema/parametros', {
                method: 'POST', body: JSON.stringify(parametros)
            });
            if (r.ok) showSnack('Parámetros guardados correctamente', 'success');
            else showSnack(r.error || 'Error guardando parámetros', 'error');
        } catch (e) {
            showSnack('Error: ' + e.message, 'error');
        } finally {
            setSavingParametros(false);
        }
    };

    const sectionButtons = [
        { key: 'mensaje', label: 'Banner Dashboard', icon: <SettingsIcon fontSize="small" /> },
        { key: 'reglas', label: 'Reglas de Acceso', icon: <SecurityIcon fontSize="small" /> },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Sub-navigation */}
            <Box sx={{ display: 'flex', gap: 1 }}>
                {sectionButtons.map(s => (
                    <Button key={s.key}
                        variant={activeSection === s.key ? 'contained' : 'outlined'}
                        startIcon={s.icon}
                        onClick={() => setActiveSection(s.key)}
                        size="small"
                        sx={{
                            textTransform: 'none', borderRadius: BORDER_RADIUS,
                            ...(activeSection === s.key
                                ? { bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }
                                : { color: PRIMARY, borderColor: PRIMARY, '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY } }
                            )
                        }}>
                        {s.label}
                    </Button>
                ))}
            </Box>

            {/* Banners del Dashboard — multi-banner con flag visible y body opcional */}
            {activeSection === 'mensaje' && (
                <Paper sx={{ p: 3, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                                Banners del Dashboard
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Avisos que aparecen en el dashboard. Solo se muestran los marcados como visibles. Si tienen "detalle al click" se abren en un modal cuando el usuario hace click.
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<AddIcon />}
                            onClick={() => setBannerEditing({ Titulo: 'SIGIF', Mensaje: '', Cuerpo_Detalle: '', Background_Color: '#1769aa', Visible: true, Orden: 0 })}
                            sx={{ bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>
                            Nuevo banner
                        </Button>
                    </Box>

                    {loadingMensaje ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: PRIMARY }} />
                        </Box>
                    ) : banners.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            No hay banners configurados. Creá uno con "Nuevo banner".
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {banners.map(b => {
                                const plainMsg = (b.Mensaje || '').replace(/<[^>]*>/g, '').trim();
                                const tieneDetalle = !!(b.Cuerpo_Detalle && b.Cuerpo_Detalle.trim());
                                return (
                                    <Paper key={b.Id} variant="outlined" sx={{
                                        p: 1.5, borderRadius: BORDER_RADIUS, display: 'flex', alignItems: 'center', gap: 1.5,
                                        opacity: b.Visible ? 1 : 0.6,
                                    }}>
                                        {/* Mini preview */}
                                        <Box sx={{
                                            minWidth: 240, maxWidth: 240, p: 1, borderRadius: '4px',
                                            background: `linear-gradient(135deg, ${b.Background_Color || '#1769aa'} 0%, #2196f3 100%)`,
                                            color: '#fff',
                                        }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, opacity: 0.9, textTransform: 'uppercase' }}>
                                                {b.Titulo || 'SIGIF'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {plainMsg || '(sin mensaje)'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip size="small" label={`#${b.Id}`} variant="outlined" />
                                                {b.Visible ? <Chip size="small" label="Visible" color="success" /> : <Chip size="small" label="Oculto" />}
                                                {tieneDetalle && <Chip size="small" label="Con detalle al click" color="info" />}
                                                <Chip size="small" label={`Orden ${b.Orden || 0}`} variant="outlined" />
                                            </Box>
                                        </Box>
                                        <Tooltip title={b.Visible ? 'Ocultar' : 'Mostrar'}>
                                            <Switch checked={!!b.Visible} onChange={() => handleToggleBanner(b)} />
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                            <IconButton onClick={() => setBannerEditing({ ...b, Cuerpo_Detalle: b.Cuerpo_Detalle || '' })}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <IconButton color="error" onClick={() => setConfirmDeleteBanner(b)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}

                    {/* Dialog crear/editar banner */}
                    <Dialog open={!!bannerEditing} onClose={() => setBannerEditing(null)} maxWidth="md" fullWidth
                        PaperProps={{ sx: { borderRadius: BORDER_RADIUS } }}>
                        <DialogTitle sx={{ fontWeight: 700 }}>
                            {bannerEditing?.Id ? `Editar banner #${bannerEditing.Id}` : 'Nuevo banner'}
                        </DialogTitle>
                        <DialogContent dividers>
                            {bannerEditing && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                                    <TextField label="Título" size="small" fullWidth
                                        value={bannerEditing.Titulo || ''}
                                        onChange={e => setBannerEditing(b => ({ ...b, Titulo: e.target.value }))}
                                        helperText="Texto chico arriba a la izquierda del banner (ej: 'SIGIF', 'AVISO')"
                                    />
                                    <TextField label="Mensaje principal" size="small" fullWidth multiline rows={2}
                                        value={(bannerEditing.Mensaje || '').replace(/<[^>]*>/g, '')}
                                        onChange={e => setBannerEditing(b => ({ ...b, Mensaje: e.target.value }))}
                                        helperText="Texto que se muestra en el banner del dashboard"
                                    />
                                    <TextField label="Detalle al click (HTML — opcional)" size="small" fullWidth multiline rows={8}
                                        value={bannerEditing.Cuerpo_Detalle || ''}
                                        onChange={e => setBannerEditing(b => ({ ...b, Cuerpo_Detalle: e.target.value }))}
                                        helperText="Si se completa, el banner se vuelve clickeable y al click abre un modal con este HTML. Para novedades, changelog, anuncios largos, etc."
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Typography variant="caption" sx={{ color: '#666' }}>Template:</Typography>
                                        <Button size="small" variant="outlined"
                                            onClick={() => setBannerEditing(b => ({
                                                ...b,
                                                Titulo: 'NOVEDADES',
                                                Mensaje: 'Novedades — Junio 2026 — Click para ver el detalle',
                                                Background_Color: b.Background_Color || '#1565c0',
                                                Cuerpo_Detalle: HTML_NOVEDADES_HISTORICO,
                                            }))}
                                            sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                                            🎉 Cargar template "Novedades" (histórico)
                                        </Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <TextField label="Color de fondo" size="small" type="color"
                                            value={bannerEditing.Background_Color || '#1769aa'}
                                            onChange={e => setBannerEditing(b => ({ ...b, Background_Color: e.target.value }))}
                                            sx={{ width: 130 }}
                                        />
                                        <TextField label="Orden" size="small" type="number"
                                            value={bannerEditing.Orden || 0}
                                            onChange={e => setBannerEditing(b => ({ ...b, Orden: e.target.value }))}
                                            sx={{ width: 100 }}
                                            helperText="Menor primero"
                                        />
                                        <FormControlLabel
                                            control={<Switch checked={!!bannerEditing.Visible}
                                                onChange={e => setBannerEditing(b => ({ ...b, Visible: e.target.checked }))} />}
                                            label={bannerEditing.Visible ? 'Visible' : 'Oculto'}
                                        />
                                    </Box>

                                    {/* Preview */}
                                    <Paper sx={{
                                        p: 1.5, borderRadius: '4px',
                                        background: `linear-gradient(135deg, ${bannerEditing.Background_Color || '#1769aa'} 0%, #2196f3 100%)`,
                                        color: '#fff',
                                    }}>
                                        <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700, textTransform: 'uppercase' }}>
                                            Vista previa
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700, opacity: 0.9 }}>
                                            {bannerEditing.Titulo || 'SIGIF'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {(bannerEditing.Mensaje || '').replace(/<[^>]*>/g, '') || '(escribí el mensaje arriba)'}
                                        </Typography>
                                        {bannerEditing.Cuerpo_Detalle && bannerEditing.Cuerpo_Detalle.trim() && (
                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, mt: 0.5 }}>
                                                ▸ click para ver más
                                            </Typography>
                                        )}
                                    </Paper>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setBannerEditing(null)}>Cancelar</Button>
                            <Button onClick={handleGuardarBanner} variant="contained" disabled={savingBanner}
                                startIcon={savingBanner ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, textTransform: 'none' }}>
                                {savingBanner ? 'Guardando…' : 'Guardar'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Confirmar eliminación */}
                    <Dialog open={!!confirmDeleteBanner} onClose={() => setConfirmDeleteBanner(null)} maxWidth="xs" fullWidth>
                        <DialogTitle>¿Eliminar banner?</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2">
                                Vas a eliminar el banner <strong>"{(confirmDeleteBanner?.Mensaje || '').replace(/<[^>]*>/g, '').slice(0, 60)}..."</strong>. Esta acción no se puede deshacer.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setConfirmDeleteBanner(null)}>Cancelar</Button>
                            <Button color="error" variant="contained" onClick={handleEliminarBanner}>Eliminar</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            )}

            {/* Reglas de Acceso */}
            {activeSection === 'reglas' && (
                <Paper sx={{ p: 3, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
                        Reglas de Acceso
                    </Typography>
                    {loadingReglas ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: PRIMARY }} />
                        </Box>
                    ) : reglas.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No hay reglas de acceso configuradas
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Referencia de reglas */}
                            <Accordion defaultExpanded={false} sx={{ borderRadius: BORDER_RADIUS, '&:before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#666' }}>
                                        Referencia de reglas ({reglas.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                                    <Table size="small">
                                        <TableBody>
                                            {reglas.map(r => (
                                                <TableRow key={r.Id}>
                                                    <TableCell sx={{ fontWeight: 700, width: 40, py: 0.5 }}>{r.Id}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.82rem', py: 0.5 }}>{r.Regla}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </AccordionDetails>
                            </Accordion>

                            {/* Selector de módulo */}
                            <TextField
                                label="Seleccionar módulo"
                                select
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={selectedModulo?.Id ?? ''}
                                onChange={e => {
                                    const mod = reglasModulos.find(m => String(m.Id) === String(e.target.value));
                                    setSelectedModulo(mod || null);
                                }}
                                SelectProps={{ native: true }}
                                sx={{
                                    maxWidth: 450,
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                                    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY }
                                }}
                            >
                                <option value="">— Seleccionar módulo —</option>
                                {reglasModulos.map(m => (
                                    <option key={m.Id} value={m.Id}>{titleCase(m.Modulo)}</option>
                                ))}
                            </TextField>

                            {/* Matriz de reglas */}
                            {selectedModulo && (
                                <>
                                    <TableContainer sx={{ borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{
                                                        fontWeight: 700, bgcolor: '#f5f5f5',
                                                        borderBottom: `2px solid ${PRIMARY}`,
                                                        position: 'sticky', left: 0, zIndex: 1
                                                    }}>
                                                        Grupo
                                                    </TableCell>
                                                    {reglas.map(r => (
                                                        <TableCell key={r.Id} sx={{
                                                            fontWeight: 700, bgcolor: '#f5f5f5',
                                                            borderBottom: `2px solid ${PRIMARY}`,
                                                            textAlign: 'center', fontSize: '0.75rem',
                                                            minWidth: 40, px: 0.5
                                                        }}>
                                                            <Tooltip title={r.Regla} arrow>
                                                                <span>{r.Id}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {reglasGrupos.map(g => (
                                                    <TableRow key={g.Id} hover>
                                                        <TableCell sx={{
                                                            fontSize: '0.82rem', fontWeight: 500,
                                                            position: 'sticky', left: 0,
                                                            bgcolor: 'background.paper', zIndex: 1
                                                        }}>
                                                            {titleCase(g.Grupo)}
                                                        </TableCell>
                                                        {reglas.map(r => (
                                                            <TableCell key={r.Id} sx={{ textAlign: 'center', px: 0, py: 0 }}>
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!matrizReglas[`${g.Id}_${r.Id}`]}
                                                                    onChange={() => toggleRegla(g.Id, r.Id)}
                                                                    sx={{
                                                                        p: 0.5,
                                                                        color: '#ccc',
                                                                        '&.Mui-checked': { color: PRIMARY }
                                                                    }}
                                                                />
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        {matrizDirty && (
                                            <Chip label="Sin guardar" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                                        )}
                                        <Button
                                            variant="contained"
                                            startIcon={savingReglas ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                            onClick={handleSaveReglas}
                                            disabled={savingReglas || !matrizDirty}
                                            sx={{
                                                bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                                                '&:hover': { bgcolor: PRIMARY_DARK }
                                            }}
                                        >
                                            {savingReglas ? 'Guardando...' : 'Guardar reglas'}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </Paper>
            )}

            {/* Parámetros */}
            {activeSection === 'parametros' && (
                <Paper sx={{ p: 3, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
                        Parámetros del Sistema
                    </Typography>
                    {loadingParametros ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: PRIMARY }} />
                        </Box>
                    ) : Object.keys(parametros).length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No hay parámetros configurados
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {Object.entries(parametros).map(([key, value]) => (
                                <TextField
                                    key={key}
                                    label={titleCase(key.replace(/_/g, ' '))}
                                    value={value ?? ''}
                                    onChange={(e) => setParametros(prev => ({ ...prev, [key]: e.target.value }))}
                                    size="small" fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: BORDER_RADIUS },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                                        '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY }
                                    }}
                                />
                            ))}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button variant="contained" startIcon={
                                    savingParametros ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                                }
                                    onClick={handleSaveParametros} disabled={savingParametros}
                                    sx={{
                                        bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                                        '&:hover': { bgcolor: PRIMARY_DARK }
                                    }}>
                                    {savingParametros ? 'Guardando...' : 'Guardar parámetros'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
}

// ═══════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════
// BIOMÉTRICO ADMIN
// ═══════════════════════════════════════════════════════════
// Extrae HH:mm de cualquier formato de hora (TIME, ISO datetime, string)
const fmtTime = (v) => {
    if (!v) return '';
    const s = String(v);
    // Si es ISO datetime (1970-01-01T07:00:00.000Z) extraer hora
    if (s.includes('T')) {
        const d = new Date(s);
        return String(d.getUTCHours()).padStart(2, '0') + ':' + String(d.getUTCMinutes()).padStart(2, '0');
    }
    // Si ya es HH:mm:ss o HH:mm
    const match = s.match(/(\d{2}):(\d{2})/);
    return match ? match[0] : s.substring(0, 5);
};

function BiometricoAdmin({ showSnack }) {
    const [section, setSection] = useState('horarios');
    const [horarios, setHorarios] = useState([]);
    const [reglas, setReglas] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dlg, setDlg] = useState({ open: false, type: '', mode: 'add', data: {}, saving: false });
    const [delConfirm, setDelConfirm] = useState({ open: false, type: '', id: null });
    const [personalLista, setPersonalLista] = useState([]);
    const [masivaDlg, setMasivaDlg] = useState({ open: false, selected: [], idHorario: '', idRegla: '', desde: '', hasta: '', saving: false });
    const [mapeo, setMapeo] = useState([]);
    const [mapeoDlg, setMapeoDlg] = useState({ open: false, empCode: '', idPersonal: null, nombreDisp: '', saving: false });

    // Cargar lista de personal para asignaciones
    useEffect(() => {
        jwtFetch('biometrico/personal-lista').then(r => { if (r.ok) setPersonalLista(r.personal || []); }).catch(() => {});
    }, []);

    const cargar = useCallback(async (sec) => {
        setLoading(true);
        try {
            const r = await jwtFetch(`biometrico/${sec}`);
            if (r.ok) {
                if (sec === 'horarios') setHorarios(r.horarios || []);
                else if (sec === 'reglas') setReglas(r.reglas || []);
                else if (sec === 'asignaciones') setAsignaciones(r.asignaciones || []);
                else if (sec === 'mapeo') setMapeo(r.mapeo || []);
            }
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        setLoading(false);
    }, [showSnack]);

    useEffect(() => {
        cargar(section);
        // Siempre cargar horarios y reglas (se usan en dropdowns de asignaciones)
        if (section !== 'horarios') cargar('horarios');
        if (section !== 'reglas') cargar('reglas');
    }, [section, cargar]);

    const openAdd = (type) => setDlg({ open: true, type, mode: 'add', data: {}, saving: false });
    const openEdit = (type, row) => {
        const data = { ...row };
        // Normalizar horas para input type="time"
        if (data.Hora_Entrada) data.Hora_Entrada = fmtTime(data.Hora_Entrada);
        if (data.Hora_Salida) data.Hora_Salida = fmtTime(data.Hora_Salida);
        setDlg({ open: true, type, mode: 'edit', data, saving: false });
    };
    const setD = (field, val) => setDlg(p => ({ ...p, data: { ...p.data, [field]: val } }));

    const saveDlg = async () => {
        setDlg(p => ({ ...p, saving: true }));
        try {
            const { type, mode, data } = dlg;
            const url = mode === 'add' ? `biometrico/${type}` : `biometrico/${type}/${data.Id}`;
            const r = await jwtFetch(url, { method: mode === 'add' ? 'POST' : 'PUT', body: JSON.stringify(data) });
            if (r.ok) { showSnack(mode === 'add' ? 'Creado' : 'Actualizado', 'success'); setDlg(p => ({ ...p, open: false })); cargar(section); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        setDlg(p => ({ ...p, saving: false }));
    };

    const handleDelete = async () => {
        try {
            const r = await jwtFetch(`biometrico/${delConfirm.type}/${delConfirm.id}`, { method: 'DELETE' });
            if (r.ok) { showSnack('Eliminado'); cargar(section); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        setDelConfirm({ open: false, type: '', id: null });
    };

    const handleGuardarMapeo = async () => {
        if (!mapeoDlg.empCode || !mapeoDlg.idPersonal) { showSnack('Complete código y persona', 'warning'); return; }
        setMapeoDlg(p => ({ ...p, saving: true }));
        try {
            const r = await jwtFetch('biometrico/mapeo', { method: 'POST', body: JSON.stringify({ empCode: mapeoDlg.empCode, idPersonal: mapeoDlg.idPersonal, nombreDisp: mapeoDlg.nombreDisp }) });
            if (r.ok) { showSnack('Mapeo guardado'); setMapeoDlg(p => ({ ...p, open: false })); cargar('mapeo'); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        setMapeoDlg(p => ({ ...p, saving: false }));
    };

    const handleEliminarMapeo = async (empCode) => {
        try {
            const r = await jwtFetch(`biometrico/mapeo/${empCode}`, { method: 'DELETE' });
            if (r.ok) { showSnack('Mapeo eliminado'); cargar('mapeo'); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
    };

    const handleMasiva = async () => {
        if (!masivaDlg.selected.length || !masivaDlg.idHorario || !masivaDlg.idRegla) { showSnack('Seleccione personas, horario y regla', 'warning'); return; }
        setMasivaDlg(p => ({ ...p, saving: true }));
        try {
            const r = await jwtFetch('biometrico/asignaciones/masiva', {
                method: 'POST', body: JSON.stringify({
                    personasIds: masivaDlg.selected.map(p => p.Id),
                    idHorario: masivaDlg.idHorario, idRegla: masivaDlg.idRegla,
                    desde: masivaDlg.desde || null, hasta: masivaDlg.hasta || null
                })
            });
            if (r.ok) { showSnack(`${r.asignados} asignaciones creadas`); setMasivaDlg(p => ({ ...p, open: false })); cargar('asignaciones'); }
            else showSnack(r.mensaje || 'Error', 'error');
        } catch (e) { showSnack('Error: ' + e.message, 'error'); }
        setMasivaDlg(p => ({ ...p, saving: false }));
    };

    const thSx2 = { fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: `2px solid ${PRIMARY}`, fontSize: '0.8rem' };
    const tdSx2 = { fontSize: '0.82rem', py: 0.5 };

    const tabs = [
        { key: 'horarios', label: 'Horarios', icon: <AccessTimeIcon fontSize="small" /> },
        { key: 'reglas', label: 'Reglas de Cálculo', icon: <SettingsIcon fontSize="small" /> },
        { key: 'asignaciones', label: 'Asignaciones', icon: <AssignmentIndIcon fontSize="small" /> },
        { key: 'mapeo', label: 'Mapeo Dispositivo', icon: <FingerprintIcon fontSize="small" /> },
    ];

    return (
        <Box>
            {/* Sub-nav */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {tabs.map(t => (
                    <Button key={t.key} variant={section === t.key ? 'contained' : 'outlined'} startIcon={t.icon}
                        onClick={() => setSection(t.key)} size="small"
                        sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS, borderColor: PRIMARY, color: section === t.key ? '#fff' : PRIMARY,
                            bgcolor: section === t.key ? PRIMARY : 'transparent', '&:hover': { bgcolor: section === t.key ? PRIMARY_DARK : PRIMARY_LIGHT } }}>
                        {t.label}
                    </Button>
                ))}
            </Box>

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box> : (
                <>
                    {/* ── HORARIOS ── */}
                    {section === 'horarios' && (
                        <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Horarios de Trabajo ({horarios.length})</Typography>
                                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openAdd('horarios')}
                                    sx={{ bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>Agregar</Button>
                            </Box>
                            <TableContainer sx={{ borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}><Table size="small">
                                <TableHead><TableRow>
                                    <TableCell sx={thSx2}>Nombre</TableCell><TableCell sx={thSx2}>Tipo</TableCell>
                                    <TableCell sx={thSx2}>Entrada</TableCell><TableCell sx={thSx2}>Salida</TableCell>
                                    <TableCell sx={thSx2}>Tiempo (min)</TableCell><TableCell sx={thSx2}>Descanso</TableCell>
                                    <TableCell sx={thSx2}>Activo</TableCell><TableCell sx={{ ...thSx2, width: 80, textAlign: 'center' }}>Acc.</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                    {horarios.length === 0 ? <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, color: '#999' }}>Sin horarios configurados</TableCell></TableRow>
                                    : horarios.map(h => (
                                        <TableRow key={h.Id} hover>
                                            <TableCell sx={{ ...tdSx2, fontWeight: 600 }}>{h.Nombre}</TableCell>
                                            <TableCell sx={tdSx2}><Chip label={h.Tipo} size="small" sx={{ borderRadius: BORDER_RADIUS, bgcolor: h.Tipo === 'Flexible' ? '#fff3e0' : '#e3f2fd', color: h.Tipo === 'Flexible' ? '#e65100' : '#1565c0', fontSize: '0.7rem' }} /></TableCell>
                                            <TableCell sx={{ ...tdSx2, fontWeight: 600 }}>{fmtTime(h.Hora_Entrada)}</TableCell>
                                            <TableCell sx={{ ...tdSx2, fontWeight: 600 }}>{fmtTime(h.Hora_Salida)}</TableCell>
                                            <TableCell sx={tdSx2}>{h.Tiempo_Trabajo}</TableCell>
                                            <TableCell sx={tdSx2}>{h.Descanso}</TableCell>
                                            <TableCell sx={tdSx2}>{h.Activo ? <Chip label="Sí" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem', borderRadius: BORDER_RADIUS }} /> : 'No'}</TableCell>
                                            <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                                                <IconButton size="small" onClick={() => openEdit('horarios', h)} sx={{ color: PRIMARY }}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" onClick={() => setDelConfirm({ open: true, type: 'horarios', id: h.Id })} sx={{ color: '#d32f2f' }}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table></TableContainer>
                        </Paper>
                    )}

                    {/* ── REGLAS ── */}
                    {section === 'reglas' && (
                        <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Reglas de Cálculo ({reglas.length})</Typography>
                                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openAdd('reglas')}
                                    sx={{ bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>Agregar</Button>
                            </Box>
                            <TableContainer sx={{ borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}><Table size="small">
                                <TableHead><TableRow>
                                    <TableCell sx={thSx2}>Nombre</TableCell><TableCell sx={thSx2}>Tolerancia</TableCell>
                                    <TableCell sx={thSx2}>Tardanza→Ausencia</TableCell><TableCell sx={thSx2}>Salida Temp.→Ausencia</TableCell>
                                    <TableCell sx={thSx2}>Sin Entrada</TableCell><TableCell sx={thSx2}>Sin Salida</TableCell>
                                    <TableCell sx={{ ...thSx2, width: 80, textAlign: 'center' }}>Acc.</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                    {reglas.length === 0 ? <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: '#999' }}>Sin reglas configuradas</TableCell></TableRow>
                                    : reglas.map(r => (
                                        <TableRow key={r.Id} hover>
                                            <TableCell sx={{ ...tdSx2, fontWeight: 600 }}>{r.Nombre}</TableCell>
                                            <TableCell sx={tdSx2}>{r.Tolerancia_Entrada} min</TableCell>
                                            <TableCell sx={tdSx2}>{r.Tardanza_Max_Ausencia} min</TableCell>
                                            <TableCell sx={tdSx2}>{r.Salida_Temprana_Max} min</TableCell>
                                            <TableCell sx={tdSx2}>{r.Sin_Entrada}</TableCell>
                                            <TableCell sx={tdSx2}>{r.Sin_Salida}</TableCell>
                                            <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                                                <IconButton size="small" onClick={() => openEdit('reglas', r)} sx={{ color: PRIMARY }}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" onClick={() => setDelConfirm({ open: true, type: 'reglas', id: r.Id })} sx={{ color: '#d32f2f' }}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table></TableContainer>
                        </Paper>
                    )}

                    {/* ── ASIGNACIONES ── */}
                    {section === 'asignaciones' && (
                        <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Asignaciones Horario/Regla ({asignaciones.length})</Typography>
                                <Button variant="outlined" size="small" startIcon={<PeopleIcon />}
                                    onClick={() => setMasivaDlg({ open: true, selected: [], idHorario: '', idRegla: '', desde: '', hasta: '', saving: false })}
                                    sx={{ borderColor: PRIMARY, color: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none' }}>Asignación Masiva</Button>
                                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openAdd('asignaciones')}
                                    sx={{ bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>Agregar</Button>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#666', mb: 1.5, display: 'block' }}>
                                Las asignaciones sin persona aplican como regla por defecto para todos. Las individuales tienen prioridad.
                            </Typography>
                            <TableContainer sx={{ borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}><Table size="small">
                                <TableHead><TableRow>
                                    <TableCell sx={thSx2}>Persona</TableCell><TableCell sx={thSx2}>Horario</TableCell>
                                    <TableCell sx={thSx2}>Regla</TableCell><TableCell sx={thSx2}>Desde</TableCell>
                                    <TableCell sx={thSx2}>Hasta</TableCell><TableCell sx={{ ...thSx2, width: 80, textAlign: 'center' }}>Acc.</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                    {asignaciones.length === 0 ? <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: '#999' }}>Sin asignaciones</TableCell></TableRow>
                                    : asignaciones.map(a => (
                                        <TableRow key={a.Id} hover sx={!a.Id_Personal ? { bgcolor: '#f3e5f5' } : {}}>
                                            <TableCell sx={{ ...tdSx2, fontWeight: 600 }}>{a.Apellido ? `${a.Apellido}, ${a.NombrePersona}` : <Chip label="POR DEFECTO" size="small" sx={{ bgcolor: '#e1bee7', color: '#6a1b9a', fontWeight: 600, fontSize: '0.7rem', borderRadius: BORDER_RADIUS }} />}</TableCell>
                                            <TableCell sx={tdSx2}>{a.Horario || '-'}</TableCell>
                                            <TableCell sx={tdSx2}>{a.Regla || '-'}</TableCell>
                                            <TableCell sx={tdSx2}>{a.Desde ? new Date(a.Desde).toLocaleDateString('es-AR') : 'Sin límite'}</TableCell>
                                            <TableCell sx={tdSx2}>{a.Hasta ? new Date(a.Hasta).toLocaleDateString('es-AR') : 'Sin límite'}</TableCell>
                                            <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                                                <IconButton size="small" onClick={() => openEdit('asignaciones', a)} sx={{ color: PRIMARY }}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" onClick={() => setDelConfirm({ open: true, type: 'asignaciones', id: a.Id })} sx={{ color: '#d32f2f' }}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table></TableContainer>
                        </Paper>
                    )}

                    {/* ── MAPEO DISPOSITIVO ── */}
                    {section === 'mapeo' && (
                        <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Mapeo ID Dispositivo → Personal SIGIF ({mapeo.length})</Typography>
                                <Button variant="contained" size="small" startIcon={<AddIcon />}
                                    onClick={() => setMapeoDlg({ open: true, empCode: '', idPersonal: null, nombreDisp: '', saving: false })}
                                    sx={{ bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>Agregar</Button>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#666', mb: 1.5, display: 'block' }}>
                                Vincula el ID del empleado en el dispositivo biométrico con el DNI de la persona en SIGIF.
                                Solo necesario para empleados que ya existían en el dispositivo con ID secuencial (no DNI).
                            </Typography>
                            <TableContainer sx={{ borderRadius: BORDER_RADIUS, border: '1px solid #e0e0e0' }}><Table size="small">
                                <TableHead><TableRow>
                                    <TableCell sx={thSx2}>ID Dispositivo</TableCell>
                                    <TableCell sx={thSx2}>Nombre en Dispositivo</TableCell>
                                    <TableCell sx={thSx2}>DNI SIGIF</TableCell>
                                    <TableCell sx={thSx2}>Persona SIGIF</TableCell>
                                    <TableCell sx={{ ...thSx2, width: 60, textAlign: 'center' }}>Acc.</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                    {mapeo.length === 0 ? <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: '#999' }}>Sin mapeos. Los empleados nuevos deben registrarse con su DNI como ID.</TableCell></TableRow>
                                    : mapeo.map(m => (
                                        <TableRow key={m.Emp_Code} hover>
                                            <TableCell sx={{ ...tdSx2, fontWeight: 700 }}>{m.Emp_Code}</TableCell>
                                            <TableCell sx={tdSx2}>{m.Nombre_Dispositivo || '-'}</TableCell>
                                            <TableCell sx={{ ...tdSx2, fontWeight: 600 }}>{m.Id_Personal}</TableCell>
                                            <TableCell sx={tdSx2}>{m.Apellido ? `${m.Apellido}, ${m.Nombre}` : <Chip label="No encontrado" size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontSize: '0.65rem', borderRadius: BORDER_RADIUS }} />}</TableCell>
                                            <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                                                <IconButton size="small" onClick={() => handleEliminarMapeo(m.Emp_Code)} sx={{ color: '#d32f2f' }}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table></TableContainer>
                        </Paper>
                    )}
                </>
            )}

            {/* Dialog Mapeo */}
            <Dialog open={mapeoDlg.open} onClose={() => setMapeoDlg(p => ({ ...p, open: false }))} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BORDER_RADIUS } }}>
                <DialogTitle sx={{ bgcolor: PRIMARY, color: '#fff', py: 1.5, px: 2, display: 'flex', alignItems: 'center' }}>
                    <FingerprintIcon sx={{ mr: 1 }} /><span style={{ fontWeight: 600, flex: 1 }}>Vincular Empleado</span>
                    <IconButton onClick={() => setMapeoDlg(p => ({ ...p, open: false }))} sx={{ color: '#fff' }} size="small"><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="ID en Dispositivo" type="number" size="small" value={mapeoDlg.empCode}
                            onChange={e => setMapeoDlg(p => ({ ...p, empCode: e.target.value }))}
                            fullWidth InputLabelProps={{ shrink: true }} helperText="El número que aparece en el biométrico para esta persona" />
                        <TextField label="Nombre en Dispositivo (referencia)" size="small" value={mapeoDlg.nombreDisp}
                            onChange={e => setMapeoDlg(p => ({ ...p, nombreDisp: e.target.value }))}
                            fullWidth InputLabelProps={{ shrink: true }} helperText="Opcional, para identificar" />
                        <Autocomplete size="small" options={personalLista}
                            getOptionLabel={o => o ? `${o.Apellido}, ${o.Nombre} (DNI: ${o.Id})` : ''}
                            value={personalLista.find(p => String(p.Id) === String(mapeoDlg.idPersonal)) || null}
                            onChange={(_, v) => setMapeoDlg(p => ({ ...p, idPersonal: v?.Id || null }))}
                            renderInput={(params) => <TextField {...params} label="Persona en SIGIF" InputLabelProps={{ shrink: true }} />}
                            isOptionEqualToValue={(o, v) => String(o.Id) === String(v?.Id)}
                            fullWidth />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button onClick={() => setMapeoDlg(p => ({ ...p, open: false }))} sx={{ color: '#666', textTransform: 'none' }}>Cancelar</Button>
                    <Button variant="contained" onClick={handleGuardarMapeo} disabled={mapeoDlg.saving}
                        startIcon={mapeoDlg.saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                        {mapeoDlg.saving ? 'Guardando...' : 'Vincular'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Add/Edit */}
            <Dialog open={dlg.open} onClose={() => setDlg(p => ({ ...p, open: false }))} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BORDER_RADIUS } }}>
                <DialogTitle sx={{ bgcolor: PRIMARY, color: '#fff', py: 1.5, px: 2, display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, flex: 1 }}>{dlg.mode === 'add' ? 'Agregar' : 'Editar'} {dlg.type === 'horarios' ? 'Horario' : dlg.type === 'reglas' ? 'Regla' : 'Asignación'}</span>
                    <IconButton onClick={() => setDlg(p => ({ ...p, open: false }))} sx={{ color: '#fff' }} size="small"><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {dlg.type === 'horarios' && (<>
                            <TextField label="Nombre" size="small" value={dlg.data.Nombre || ''} onChange={e => setD('Nombre', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="Tipo" select size="small" value={dlg.data.Tipo || 'Normal'} onChange={e => setD('Tipo', e.target.value)} SelectProps={{ native: true }} fullWidth InputLabelProps={{ shrink: true }}>
                                <option value="Normal">Normal</option><option value="Flexible">Flexible</option>
                            </TextField>
                            <TextField label="Hora Entrada" type="time" size="small" value={dlg.data.Hora_Entrada || ''} onChange={e => setD('Hora_Entrada', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="Hora Salida" type="time" size="small" value={dlg.data.Hora_Salida || ''} onChange={e => setD('Hora_Salida', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="Tiempo Trabajo (min)" type="number" size="small" value={dlg.data.Tiempo_Trabajo || ''} onChange={e => setD('Tiempo_Trabajo', parseInt(e.target.value) || 0)} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="Descanso (min)" type="number" size="small" value={dlg.data.Descanso || ''} onChange={e => setD('Descanso', parseInt(e.target.value) || 0)} fullWidth InputLabelProps={{ shrink: true }} />
                        </>)}
                        {dlg.type === 'reglas' && (<>
                            <TextField label="Nombre" size="small" value={dlg.data.Nombre || ''} onChange={e => setD('Nombre', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="Tolerancia Entrada (min)" type="number" size="small" value={dlg.data.Tolerancia_Entrada ?? 10} onChange={e => setD('Tolerancia_Entrada', parseInt(e.target.value) || 0)} fullWidth InputLabelProps={{ shrink: true }} helperText="Minutos de gracia antes de contar tardanza" />
                            <TextField label="Tardanza → Ausencia (min)" type="number" size="small" value={dlg.data.Tardanza_Max_Ausencia ?? 90} onChange={e => setD('Tardanza_Max_Ausencia', parseInt(e.target.value) || 0)} fullWidth InputLabelProps={{ shrink: true }} helperText="Si la tardanza excede estos minutos, cuenta como ausencia" />
                            <TextField label="Salida Temprana → Ausencia (min)" type="number" size="small" value={dlg.data.Salida_Temprana_Max ?? 120} onChange={e => setD('Salida_Temprana_Max', parseInt(e.target.value) || 0)} fullWidth InputLabelProps={{ shrink: true }} helperText="Si se va X min antes, cuenta como ausencia" />
                            <TextField label="Sin fichaje de entrada" select size="small" value={dlg.data.Sin_Entrada || 'Ausencia'} onChange={e => setD('Sin_Entrada', e.target.value)} SelectProps={{ native: true }} fullWidth InputLabelProps={{ shrink: true }}>
                                <option value="Ausencia">Ausencia</option><option value="Tardanza">Tardanza</option>
                            </TextField>
                            <TextField label="Sin fichaje de salida" select size="small" value={dlg.data.Sin_Salida || 'Incompleto'} onChange={e => setD('Sin_Salida', e.target.value)} SelectProps={{ native: true }} fullWidth InputLabelProps={{ shrink: true }}>
                                <option value="Incompleto">Incompleto</option><option value="Ausencia">Ausencia</option>
                            </TextField>
                        </>)}
                        {dlg.type === 'asignaciones' && (<>
                            <Autocomplete size="small" options={personalLista}
                                getOptionLabel={o => o ? `${o.Apellido}, ${o.Nombre} (${o.Id})` : ''}
                                value={personalLista.find(p => String(p.Id) === String(dlg.data.Id_Personal)) || null}
                                onChange={(_, v) => setD('Id_Personal', v?.Id || null)}
                                renderInput={(params) => <TextField {...params} label="Persona (vacío = por defecto)" InputLabelProps={{ shrink: true }} helperText="Dejar vacío para que aplique a todos" />}
                                isOptionEqualToValue={(o, v) => String(o.Id) === String(v?.Id)}
                                fullWidth />
                            <TextField label="Horario" select size="small" value={dlg.data.Id_Horario || ''} onChange={e => setD('Id_Horario', e.target.value)} SelectProps={{ native: true }} fullWidth InputLabelProps={{ shrink: true }}>
                                <option value="">— Seleccionar —</option>
                                {horarios.map(h => <option key={h.Id} value={h.Id}>{h.Nombre} ({fmtTime(h.Hora_Entrada)}-{fmtTime(h.Hora_Salida)})</option>)}
                            </TextField>
                            <TextField label="Regla" select size="small" value={dlg.data.Id_Regla || ''} onChange={e => setD('Id_Regla', e.target.value)} SelectProps={{ native: true }} fullWidth InputLabelProps={{ shrink: true }}>
                                <option value="">— Seleccionar —</option>
                                {reglas.map(r => <option key={r.Id} value={r.Id}>{r.Nombre}</option>)}
                            </TextField>
                            <TextField label="Desde" type="date" size="small" value={(dlg.data.Desde || '').substring(0, 10)} onChange={e => setD('Desde', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} helperText="Opcional" />
                            <TextField label="Hasta" type="date" size="small" value={(dlg.data.Hasta || '').substring(0, 10)} onChange={e => setD('Hasta', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} helperText="Opcional" />
                        </>)}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button onClick={() => setDlg(p => ({ ...p, open: false }))} sx={{ color: '#666', textTransform: 'none' }}>Cancelar</Button>
                    <Button variant="contained" onClick={saveDlg} disabled={dlg.saving}
                        startIcon={dlg.saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                        {dlg.saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Delete */}
            <Dialog open={delConfirm.open} onClose={() => setDelConfirm({ open: false, type: '', id: null })} PaperProps={{ sx: { borderRadius: BORDER_RADIUS } }}>
                <DialogTitle sx={{ bgcolor: '#d32f2f', color: '#fff', py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600 }}>Confirmar eliminación</DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}><Typography>¿Está seguro que desea eliminar este registro?</Typography></DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button onClick={() => setDelConfirm({ open: false, type: '', id: null })} sx={{ color: '#666', textTransform: 'none' }}>Cancelar</Button>
                    <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#d32f2f', textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: '#b71c1c' } }}>Eliminar</Button>
                </DialogActions>
            </Dialog>
            {/* Asignación Masiva Dialog */}
            <Dialog open={masivaDlg.open} onClose={() => setMasivaDlg(p => ({ ...p, open: false }))} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: BORDER_RADIUS } }}>
                <DialogTitle sx={{ bgcolor: PRIMARY, color: '#fff', py: 1.5, px: 2, display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <span style={{ fontWeight: 600, flex: 1 }}>Asignación Masiva</span>
                    <IconButton onClick={() => setMasivaDlg(p => ({ ...p, open: false }))} sx={{ color: '#fff' }} size="small"><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Autocomplete multiple size="small" options={personalLista}
                            getOptionLabel={o => o ? `${o.Apellido}, ${o.Nombre} (${o.Id})` : ''}
                            value={masivaDlg.selected}
                            onChange={(_, v) => setMasivaDlg(p => ({ ...p, selected: v }))}
                            renderInput={(params) => <TextField {...params} label={`Personal (${masivaDlg.selected.length} seleccionados)`} InputLabelProps={{ shrink: true }} />}
                            isOptionEqualToValue={(o, v) => String(o.Id) === String(v?.Id)}
                            disableCloseOnSelect
                            renderOption={(props, option, { selected }) => (
                                <li {...props}><Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0, '&.Mui-checked': { color: PRIMARY } }} />{option.Apellido}, {option.Nombre} — <span style={{ color: '#999', marginLeft: 4, fontSize: '0.8rem' }}>{option.Funcion || ''} | {option.Area || ''}</span></li>
                            )}
                            fullWidth />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Horario" select size="small" value={masivaDlg.idHorario}
                                onChange={e => setMasivaDlg(p => ({ ...p, idHorario: e.target.value }))}
                                SelectProps={{ native: true }} fullWidth InputLabelProps={{ shrink: true }}>
                                <option value="">— Seleccionar —</option>
                                {horarios.map(h => <option key={h.Id} value={h.Id}>{h.Nombre} ({fmtTime(h.Hora_Entrada)}-{fmtTime(h.Hora_Salida)})</option>)}
                            </TextField>
                            <TextField label="Regla" select size="small" value={masivaDlg.idRegla}
                                onChange={e => setMasivaDlg(p => ({ ...p, idRegla: e.target.value }))}
                                SelectProps={{ native: true }} fullWidth InputLabelProps={{ shrink: true }}>
                                <option value="">— Seleccionar —</option>
                                {reglas.map(r => <option key={r.Id} value={r.Id}>{r.Nombre}</option>)}
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Desde (opcional)" type="date" size="small" value={masivaDlg.desde}
                                onChange={e => setMasivaDlg(p => ({ ...p, desde: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="Hasta (opcional)" type="date" size="small" value={masivaDlg.hasta}
                                onChange={e => setMasivaDlg(p => ({ ...p, hasta: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button onClick={() => setMasivaDlg(p => ({ ...p, open: false }))} sx={{ color: '#666', textTransform: 'none' }}>Cancelar</Button>
                    <Button variant="contained" onClick={handleMasiva} disabled={masivaDlg.saving}
                        startIcon={masivaDlg.saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                        {masivaDlg.saving ? 'Asignando...' : `Asignar a ${masivaDlg.selected.length} personas`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ═══════════════════════════════════════════════════════════
// AUDITORÍA ADMIN
// ═══════════════════════════════════════════════════════════
const ENTIDADES = ['', 'USUARIO', 'GRUPO', 'SUBGRUPO', 'PERMISOS', 'CATALOGO', 'SISTEMA'];
const ACCIONES_MAP = {
    CREAR_USUARIO: { label: 'Crear usuario', color: '#2e7d32' },
    EDITAR_USUARIO: { label: 'Editar usuario', color: '#1565c0' },
    CAMBIAR_PASSWORD: { label: 'Cambiar contraseña', color: '#f57f17' },
    CREAR_GRUPO: { label: 'Crear grupo', color: '#2e7d32' },
    EDITAR_GRUPO: { label: 'Editar grupo', color: '#1565c0' },
    ELIMINAR_GRUPO: { label: 'Eliminar grupo', color: '#c62828' },
    CREAR_SUBGRUPO: { label: 'Crear subgrupo', color: '#2e7d32' },
    EDITAR_SUBGRUPO: { label: 'Editar subgrupo', color: '#1565c0' },
    ELIMINAR_SUBGRUPO: { label: 'Eliminar subgrupo', color: '#c62828' },
    GUARDAR_PERMISOS: { label: 'Guardar permisos', color: '#6a1b9a' },
    COPIAR_PERMISOS: { label: 'Copiar permisos', color: '#6a1b9a' },
    ASIGNAR_MASIVO: { label: 'Asignar masivo', color: '#6a1b9a' },
    CREAR_CATALOGO: { label: 'Crear catálogo', color: '#2e7d32' },
    EDITAR_CATALOGO: { label: 'Editar catálogo', color: '#1565c0' },
    ELIMINAR_CATALOGO: { label: 'Eliminar catálogo', color: '#c62828' },
    EDITAR_BANNER: { label: 'Editar banner', color: '#00695c' },
};

function AuditoriaAdmin() {
    const [registros, setRegistros] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const pageSize = 30;
    const [filtros, setFiltros] = useState({ entidad: '', accion: '', usuario: '', desde: '', hasta: '' });

    const cargar = useCallback(async (p, f) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: p, pageSize });
            if (f.entidad) params.append('entidad', f.entidad);
            if (f.accion) params.append('accion', f.accion);
            if (f.usuario) params.append('usuario', f.usuario);
            if (f.desde) params.append('desde', f.desde);
            if (f.hasta) params.append('hasta', f.hasta);
            const r = await jwtFetch(`permisos/auditoria?${params.toString()}`);
            if (r.ok) {
                setRegistros(r.registros || []);
                setTotal(r.total || 0);
            }
        } catch { }
        setLoading(false);
    }, []);

    useEffect(() => { cargar(page, filtros); }, [page, cargar]); // eslint-disable-line

    const buscar = () => { setPage(1); cargar(1, filtros); };
    const totalPages = Math.ceil(total / pageSize) || 1;

    const formatFecha = (f) => {
        if (!f) return '-';
        const d = new Date(f);
        return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
            + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    return (
        <Box>
            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: BORDER_RADIUS }}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label="Usuario"
                        size="small"
                        value={filtros.usuario}
                        onChange={e => setFiltros(p => ({ ...p, usuario: e.target.value }))}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        label="Entidad"
                        size="small"
                        select
                        InputLabelProps={{ shrink: true }}
                        value={filtros.entidad}
                        onChange={e => setFiltros(p => ({ ...p, entidad: e.target.value }))}
                        sx={{ width: 150 }}
                        SelectProps={{ native: true }}
                    >
                        <option value="">Todas</option>
                        {ENTIDADES.filter(Boolean).map(e => <option key={e} value={e}>{e}</option>)}
                    </TextField>
                    <TextField
                        label="Acción"
                        size="small"
                        value={filtros.accion}
                        onChange={e => setFiltros(p => ({ ...p, accion: e.target.value }))}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        label="Desde"
                        type="date"
                        size="small"
                        value={filtros.desde}
                        onChange={e => setFiltros(p => ({ ...p, desde: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 150 }}
                    />
                    <TextField
                        label="Hasta"
                        type="date"
                        size="small"
                        value={filtros.hasta}
                        onChange={e => setFiltros(p => ({ ...p, hasta: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 150 }}
                    />
                    <Button variant="contained" size="small" onClick={buscar}
                        startIcon={<SearchIcon />}
                        sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }, textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                        Buscar
                    </Button>
                    <Typography variant="body2" sx={{ color: '#666', ml: 'auto' }}>
                        {total} registro{total !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            </Paper>

            {/* Tabla */}
            <TableContainer component={Paper} sx={{ borderRadius: BORDER_RADIUS }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 700, width: 150 }}>Fecha</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 140 }}>Usuario</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 160 }}>Acción</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 100 }}>Entidad</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Detalle</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                    <CircularProgress size={28} sx={{ color: PRIMARY }} />
                                </TableCell>
                            </TableRow>
                        ) : registros.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                                    No hay registros de auditoría
                                </TableCell>
                            </TableRow>
                        ) : registros.map((r, i) => {
                            const accionInfo = ACCIONES_MAP[r.Accion] || { label: r.Accion, color: '#666' };
                            return (
                                <TableRow key={r.Id || i} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                    <TableCell sx={{ fontSize: '0.8rem', color: '#555' }}>
                                        {formatFecha(r.Fecha)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                        {r.Usuario_Nombre || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={accionInfo.label}
                                            size="small"
                                            sx={{
                                                bgcolor: accionInfo.color + '18',
                                                color: accionInfo.color,
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                                height: 24,
                                                borderRadius: BORDER_RADIUS
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', color: '#666' }}>
                                        {r.Entidad}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', color: '#444' }}>
                                        {r.Detalle}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Paginación */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                    <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        <NavigateBeforeIcon />
                    </IconButton>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        Página {page} de {totalPages}
                    </Typography>
                    <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                        <NavigateNextIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}

// ═══════════════════════════════════════════════════════════
// MAIN ADMIN HUB
// ═══════════════════════════════════════════════════════════
const NAV_ITEMS = [
    { key: 'usuarios', label: 'Usuarios y Permisos', icon: <PeopleIcon /> },
    { key: 'solicitudes-reset', label: 'Solicitudes de Reset', icon: <LockResetIcon /> },
    { key: 'catalogos', label: 'Catálogos', icon: <ListAltIcon /> },
    { key: 'sistema', label: 'Sistema', icon: <SettingsIcon /> },
    { key: 'auditoria', label: 'Auditoría', icon: <HistoryIcon /> },
    { key: 'biometrico', label: 'Biométrico', icon: <FingerprintIcon /> },
];

// ═══════════════════════════════════════════════════════════
// SOLICITUDES DE RESET — vista de admin
// ═══════════════════════════════════════════════════════════
function SolicitudesResetAdmin({ showSnack }) {
    const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [procesarOpen, setProcesarOpen] = useState(false);
    const [solicitudActiva, setSolicitudActiva] = useState(null);
    const [accion, setAccion] = useState('RESET_PASSWORD');
    const [nota, setNota] = useState('');
    const [procesando, setProcesando] = useState(false);
    const [resultado, setResultado] = useState(null); // { passwordTemporal, idUsuario, mensaje }

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await solicitudesResetListarJWT(filtroEstado === 'TODOS' ? null : filtroEstado);
            setSolicitudes(resp?.solicitudes || []);
        } catch (e) {
            showSnack('Error al cargar solicitudes', 'error');
        } finally {
            setLoading(false);
        }
    }, [filtroEstado, showSnack]);

    useEffect(() => { cargar(); }, [cargar]);

    const abrirProcesar = (sol) => {
        setSolicitudActiva(sol);
        setAccion(sol.IdUsuarioMatcheado
            ? (sol.Tipo === 'PASSWORD' ? 'RESET_PASSWORD' : 'ENVIAR_USUARIO')
            : 'RECHAZAR');
        setNota('');
        setResultado(null);
        setProcesarOpen(true);
    };
    const cerrarProcesar = () => {
        setProcesarOpen(false);
        setSolicitudActiva(null);
        setResultado(null);
    };
    const confirmarProcesar = async () => {
        if (!solicitudActiva) return;
        setProcesando(true);
        try {
            const resp = await solicitudesResetProcesarJWT(solicitudActiva.Id, { accion, nota });
            if (resp?.ok) {
                if (resp.passwordTemporal || resp.idUsuario) {
                    // Mostramos los datos sensibles dentro del dialog (una sola vez).
                    setResultado({
                        passwordTemporal: resp.passwordTemporal || null,
                        idUsuario: resp.idUsuario || null,
                        mensaje: resp.mensaje || 'Procesada',
                    });
                    cargar();
                } else {
                    showSnack(resp.mensaje || 'Procesada', 'success');
                    cerrarProcesar();
                    cargar();
                }
            } else {
                showSnack(resp?.mensaje || 'Error al procesar', 'error');
            }
        } catch (e) {
            showSnack('Error al procesar la solicitud', 'error');
        } finally {
            setProcesando(false);
        }
    };
    const copiar = async (texto) => {
        try {
            await navigator.clipboard.writeText(texto);
            showSnack('Copiado al portapapeles', 'success');
        } catch {
            showSnack('No se pudo copiar', 'error');
        }
    };

    const fmtFecha = (d) => {
        const x = parseFechaServer(d);
        return x ? x.toLocaleString('es-AR') : '';
    };
    const colorEstado = (e) => e === 'PENDIENTE' ? '#e65100' : e === 'PROCESADO' ? '#2e7d32' : '#757575';

    return (
        <Box>
            <Paper sx={{ p: 1.5, mb: 2, borderRadius: BORDER_RADIUS, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }} elevation={0} variant="outlined">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Estado:</Typography>
                {['PENDIENTE', 'PROCESADO', 'RECHAZADO', 'TODOS'].map(e => (
                    <Chip
                        key={e}
                        label={e}
                        size="small"
                        onClick={() => setFiltroEstado(e)}
                        color={filtroEstado === e ? 'primary' : 'default'}
                        variant={filtroEstado === e ? 'filled' : 'outlined'}
                    />
                ))}
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={cargar} disabled={loading} variant="outlined">
                    Refrescar
                </Button>
            </Paper>

            {loading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress size={28} /></Box>}

            {!loading && solicitudes.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: '#888' }}>
                    No hay solicitudes para este filtro.
                </Paper>
            )}

            {!loading && solicitudes.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: BORDER_RADIUS }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Lo ingresado</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Usuario matcheado</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Persona</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>IP</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Acción</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {solicitudes.map(s => (
                                <TableRow key={s.Id} hover>
                                    <TableCell sx={{ fontSize: 12 }}>{fmtFecha(s.FechaSolicitud)}</TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>
                                        <Chip label={s.Tipo} size="small" color={s.Tipo === 'PASSWORD' ? 'warning' : 'info'} variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{s.DniOrUsuarioIngresado}</TableCell>
                                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                                        {s.IdUsuarioMatcheado || <span style={{ color: '#d32f2f' }}>SIN MATCH</span>}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>{s.NombrePersonal || '-'}</TableCell>
                                    <TableCell sx={{ fontSize: 11, color: '#666' }}>{s.IP || '-'}</TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>
                                        <span style={{ color: colorEstado(s.Estado), fontWeight: 600 }}>{s.Estado}</span>
                                        {s.UsuarioProcesador && (
                                            <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                                                {s.AccionProcesador} por {s.UsuarioProcesador}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {s.Estado === 'PENDIENTE' && (
                                            <Button size="small" variant="contained" onClick={() => abrirProcesar(s)} sx={{ textTransform: 'none' }}>
                                                Procesar
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog procesar */}
            <Dialog open={procesarOpen} onClose={procesando ? undefined : cerrarProcesar} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BORDER_RADIUS } }}>
                <DialogTitle sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockResetIcon /> Procesar solicitud
                </DialogTitle>
                <DialogContent sx={{ pt: '20px !important' }}>
                    {solicitudActiva && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: BORDER_RADIUS }}>
                            <Typography variant="body2"><b>Persona:</b> {solicitudActiva.NombrePersonal || '-'}</Typography>
                            <Typography variant="body2"><b>Usuario:</b> <code>{solicitudActiva.IdUsuarioMatcheado || 'SIN MATCH'}</code></Typography>
                            <Typography variant="body2"><b>Solicitó:</b> {solicitudActiva.Tipo === 'PASSWORD' ? 'Resetear contraseña' : 'Recuperar usuario'}</Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                Lo ingresado: <code>{solicitudActiva.DniOrUsuarioIngresado}</code> · IP {solicitudActiva.IP || '-'} · {fmtFecha(solicitudActiva.FechaSolicitud)}
                            </Typography>
                        </Box>
                    )}

                    {resultado ? (
                        <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>{resultado.mensaje}</Alert>
                            {resultado.idUsuario && (
                                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderRadius: BORDER_RADIUS }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>Usuario:</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>{resultado.idUsuario}</Typography>
                                    <IconButton size="small" onClick={() => copiar(resultado.idUsuario)}><ContentCopyIcon fontSize="small" /></IconButton>
                                </Paper>
                            )}
                            {resultado.passwordTemporal && (
                                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderRadius: BORDER_RADIUS, bgcolor: '#fff3e0' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>Contraseña:</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 16, letterSpacing: 1, flex: 1 }}>{resultado.passwordTemporal}</Typography>
                                    <IconButton size="small" onClick={() => copiar(resultado.passwordTemporal)}><ContentCopyIcon fontSize="small" /></IconButton>
                                </Paper>
                            )}
                            <Alert severity="warning" sx={{ fontSize: 13 }}>
                                Esta información se muestra una sola vez. Comunicale al usuario por canal externo (teléfono / WhatsApp / en persona). El sistema le va a pedir cambiar la contraseña al primer login.
                            </Alert>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Acción:</Typography>
                            {solicitudActiva?.IdUsuarioMatcheado && solicitudActiva.Tipo === 'PASSWORD' && (
                                <FormControlLabel
                                    sx={{ display: 'flex' }}
                                    control={<Checkbox checked={accion === 'RESET_PASSWORD'} onChange={() => setAccion('RESET_PASSWORD')} />}
                                    label={<span><b>Resetear contraseña</b> — genera una contraseña temporal para entregar al usuario</span>}
                                />
                            )}
                            {solicitudActiva?.IdUsuarioMatcheado && (
                                <FormControlLabel
                                    sx={{ display: 'flex' }}
                                    control={<Checkbox checked={accion === 'ENVIAR_USUARIO'} onChange={() => setAccion('ENVIAR_USUARIO')} />}
                                    label={<span><b>Enviar usuario</b> — marca la solicitud como procesada (el username se muestra acá para que se lo comuniques)</span>}
                                />
                            )}
                            <FormControlLabel
                                sx={{ display: 'flex' }}
                                control={<Checkbox checked={accion === 'RECHAZAR'} onChange={() => setAccion('RECHAZAR')} />}
                                label={<span><b>Rechazar</b> — solicitud sospechosa o sin match</span>}
                            />

                            <TextField
                                label="Nota interna (opcional)"
                                value={nota}
                                onChange={e => setNota(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                sx={{ mt: 1.5 }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {resultado ? (
                        <Button variant="contained" onClick={cerrarProcesar} sx={{ textTransform: 'none' }}>
                            Cerrar
                        </Button>
                    ) : (
                        <>
                            <Button onClick={cerrarProcesar} disabled={procesando} sx={{ textTransform: 'none' }}>Cancelar</Button>
                            <Button
                                variant="contained"
                                onClick={confirmarProcesar}
                                disabled={procesando}
                                startIcon={procesando ? <CircularProgress size={14} color="inherit" /> : null}
                                sx={{ textTransform: 'none', bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}
                            >
                                Confirmar
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default function AdminHub() {
    const { tienePermiso } = usePermisos();
    const puedeAcceder = tienePermiso('ADMINISTRACION DE PERMISOS', 'SELECT');
    const [activeSection, setActiveSection] = useState('usuarios');
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    const showSnack = useCallback((msg, severity = 'success') => {
        setSnack({ open: true, msg, severity });
    }, []);

    if (!puedeAcceder) {
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '60vh', flexDirection: 'column', gap: 2
            }}>
                <SecurityIcon sx={{ fontSize: 64, color: '#ccc' }} />
                <Typography variant="h6" color="text.secondary">
                    No tiene permisos para acceder a esta sección
                </Typography>
            </Box>
        );
    }

    const isStandalone = window.location.pathname.startsWith('/admin');

    return (
        <Box sx={{
            display: 'flex',
            height: isStandalone ? '100vh' : 'calc(100vh - 64px)',
            bgcolor: '#f5f5f5',
            position: isStandalone ? 'fixed' : 'relative',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: isStandalone ? 1300 : 'auto',
        }}>
            {/* Left Navigation Panel */}
            <Paper sx={{
                width: NAV_WIDTH, minWidth: NAV_WIDTH, flexShrink: 0,
                borderRadius: 0, borderRight: '1px solid #e0e0e0',
                display: 'flex', flexDirection: 'column',
                position: 'sticky', top: 0, height: '100%',
                overflow: 'hidden'
            }} elevation={0}>
                {/* Header */}
                <Box sx={{
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                    color: '#fff', px: 2, py: 2,
                    display: 'flex', alignItems: 'center', gap: 1
                }}>
                    <SettingsIcon sx={{ fontSize: '1.3rem' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
                        Panel de<br />Administración
                    </Typography>
                </Box>

                {/* Navigation Items */}
                <List sx={{ py: 1, flex: 1 }}>
                    {NAV_ITEMS.map((item) => (
                        <ListItemButton
                            key={item.key}
                            selected={activeSection === item.key}
                            onClick={() => setActiveSection(item.key)}
                            sx={{
                                py: 1.5, px: 2, mx: 0.5, my: 0.25,
                                borderRadius: BORDER_RADIUS,
                                '&.Mui-selected': {
                                    bgcolor: PRIMARY_LIGHT,
                                    borderLeft: `3px solid ${PRIMARY}`,
                                    '&:hover': { bgcolor: PRIMARY_LIGHT }
                                },
                                '&:hover': { bgcolor: '#fafafa' }
                            }}>
                            <ListItemIcon sx={{
                                minWidth: 36,
                                color: activeSection === item.key ? PRIMARY : '#666'
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontSize: '0.85rem',
                                    fontWeight: activeSection === item.key ? 700 : 500,
                                    color: activeSection === item.key ? PRIMARY : '#333'
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>

                <Divider />
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                        SIGIF Administración v2.0
                    </Typography>
                </Box>
            </Paper>

            {/* Main Content Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                {/* Section Header */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', fontSize: '1.3rem' }}>
                        {NAV_ITEMS.find(n => n.key === activeSection)?.label || ''}
                    </Typography>
                    <Divider sx={{ mt: 1 }} />
                </Box>

                {/* Section Content */}
                {activeSection === 'usuarios' && <UsuariosPermisos />}
                {activeSection === 'solicitudes-reset' && <SolicitudesResetAdmin showSnack={showSnack} />}
                {activeSection === 'catalogos' && <CatalogosAdmin showSnack={showSnack} />}
                {activeSection === 'sistema' && <SistemaConfig showSnack={showSnack} />}
                {activeSection === 'auditoria' && <AuditoriaAdmin />}
                {activeSection === 'biometrico' && <BiometricoAdmin showSnack={showSnack} />}
            </Box>

            {/* Snackbar */}
            <SnackbarNotif
                open={snack.open}
                msg={snack.msg}
                severity={snack.severity}
                onClose={() => setSnack(prev => ({ ...prev, open: false }))}
            />
        </Box>
    );
}
